import { useState, useEffect, useCallback, useRef, useMemo, type FC, type ReactNode, type Dispatch, type SetStateAction, type RefObject } from "react";
import moment from "moment-timezone";
import { useEditor, Tiptap, TiptapContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Paragraph from "@tiptap/extension-paragraph";
import { TableKit } from "@tiptap/extension-table";
import Image from "@tiptap/extension-image";
import mammoth from "mammoth";
import { marked } from "marked";
import * as pdfjsLib from "pdfjs-dist/webpack.mjs";
import { useLoadCommunity } from "../../hooks/authhooks";
import { useGetMemberAgreement } from "../../hooks/communityhook";
import { useLoadCommunityResources } from "../../hooks/resourcehooks";
import { useLoadMemberships, useLoadRequests, useSendMessage } from "../../hooks/memberhooks";
import type { ResourceSchema, AgreementSignatoryInfo, CommunityAgreementSchema, PlanSchema, PlanRecurrenceOptions, RequirementSchema, ResourcePlanSchema, DiscountSchema, MembershipSchema, CurrentRequirementsSchema } from "../../data/types";
import { toast } from "sonner";
import { MemberAgreementPreviewModal, MemberDetailsModal } from "../../molecules/AppModal";
import { MEMBERSHIP_AGREEMENT_REQUIREMENT_ID } from "../../molecules/MemberDetails";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryIcon from "@mui/icons-material/History";
import EditIcon from "@mui/icons-material/Edit";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatStrikethroughIcon from "@mui/icons-material/FormatStrikethrough";
import TitleIcon from "@mui/icons-material/Title";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  styleThousandCommas,
  planRecurrenceMap,
  sortAgreementRows,
  getDefaultAgreementSortDirection,
  type AgreementSortField,
} from "../../utils/helpers";
import SortableHeader, { type SortDirection } from "../../components/SortableHeader";
// import LoadingScreen from "../shared/LoadingScreen";

/** Paragraph extension that preserves class so signatory-typewriter renders in typewriter font. */
const ParagraphWithClass = Paragraph.extend({
  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute("class"),
        renderHTML: (attributes) => (attributes.class ? { class: attributes.class } : {}),
      },
    };
  },
});

const ACCEPTED_TYPES =
  ".doc,.docx,.pdf,.md,.html,.htm,.odt,.rtf,.txt,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,text/markdown,text/html,text/plain,application/rtf";

const DOL_TEMPLATE_URL = "/templates/DOLMemberAgreement.docx";

const DOL_SAMPLE_FULL_ADDRESS = "1231-B Marion Barry Ave SE, Washington, DC 20020";
const DOL_SAMPLE_STREET = "1231-B Marion Barry Ave SE,";
const DOL_SAMPLE_CITY_STATE_ZIP = "Washington, DC 20020";

type CommunityForTemplate = {
  name?: string;
  address?: string;
  city?: string;
  state?: string | string[];
  zipcode?: string;
};

/** Formats community address as "address, city, state zipcode". */
const formatCommunityAddress = (c: CommunityForTemplate | null | undefined): string => {
  if (!c) return "";
  const state = Array.isArray(c.state) ? c.state[0] : c.state;
  const parts = [c.address, c.city, state, c.zipcode].filter(Boolean).map(String).map((s) => s.trim());
  return parts.join(", ");
}

/** Formats "city, state zipcode" for replacement when address is split in template. */
const formatCityStateZip = (c: CommunityForTemplate | null | undefined): string => {
  if (!c) return "";
  const state = Array.isArray(c.state) ? c.state[0] : c.state;
  const stateZip = [state, c.zipcode].filter(Boolean).map(String).map((s) => s.trim()).join(" ");
  const parts = [c.city, stateZip].filter(Boolean);
  return parts.join(", ");
}

/** Removes all <img> elements from HTML (so DOCX-embedded images are not included; only injected logo is used). */
const stripImagesFromHtml = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  doc.querySelectorAll("img").forEach((el) => el.remove());
  return doc.body?.innerHTML ?? html;
}

/** Replaces DOL template placeholders with community name, address, and optional signatory info. */
const applyCommunityToDOLTemplate = (
  html: string,
  community: CommunityForTemplate | null | undefined,
  signatoryInfo?: AgreementSignatoryInfo | null
): string => {
  if (!community) return html;
  // const name = (community.name ?? "").trim();
  const fullAddress = formatCommunityAddress(community);
  const streetOnly = (community.address ?? "").trim();
  const cityStateZip = formatCityStateZip(community);
  let out = html;
  if (fullAddress) {
    out = out.split(DOL_SAMPLE_FULL_ADDRESS).join(fullAddress);
  }
  if (streetOnly) {
    out = out.split(DOL_SAMPLE_STREET).join(streetOnly ? `${streetOnly},` : "");
  }
  if (cityStateZip) {
    out = out.split(DOL_SAMPLE_CITY_STATE_ZIP).join(cityStateZip);
  }
  if (community?.state) {
    out = out.split("DC").join(Array.isArray(community.state) ? community.state[0] : community.state);
    out = out.split("District of Columbia").join(Array.isArray(community.state) ? community.state[0] : community.state);
    out = out.split("D.C.").join(Array.isArray(community.state) ? community.state[0] : community.state);
  }

  if (signatoryInfo?.adminSignatoryName) {
    out = out.split("Christopher Bradshaw").join(signatoryInfo.adminSignatoryName);
  } else {
    out = out.replace(/\bChristopher Bradshaw\b/g, "[SIGNATORY NAME]");
  }
  if (signatoryInfo?.adminSignatoryJobTitle) {
    out = out.split("Executive Director").join(signatoryInfo.adminSignatoryJobTitle);
  } else {
    out = out.replace(/\bExecutive Director\b/g, "[SIGNATORY JOB TITLE]");
  }

  if (signatoryInfo?.registeredBusinessName) {
    out = out.split("Dreaming Out Loud").join(signatoryInfo.registeredBusinessName);
    out = out.split("DOL Kitchen").join(`${signatoryInfo.registeredBusinessName} Kitchen`);
    out = out.replace(/\bDOL\b/g, signatoryInfo.registeredBusinessName);
    out = out.replace(/\bDol\b/g, signatoryInfo.registeredBusinessName);
    
  } else {
    out = out.split("Dreaming Out Loud").join("[BUSINESS NAME]");
    out = out.split("DOL Kitchen").join(`[BUSINESS NAME] Kitchen`);
    out = out.replace(/\bDOL\b/g, "[BUSINESS NAME]");
    out = out.replace(/\bDol\b/g, "[BUSINESS NAME]");
  }

  if (signatoryInfo?.registeredBusinessEntity) {
    out = out.split("Inc.").join(`${signatoryInfo.registeredBusinessEntity}.`);
  } else {
    out = out.split("Inc.").join("");
  }

  // Fix typo "15t5" -> "15th" (template or mammoth output); plain string and when "t5" is inside a tag e.g. 15<sup>t5</sup>
  out = out.replace(/15t5/g, "15th");
  out = out.replace(/15(<[^>]+>)t5(<\/[^>]+>)/g, "15$1th$2");
  out = out.replace(/\b1301 Good Hope Road LLC\b/g, "Facility");

  // Insert signatory name and job title right above the last "Signatory Name and title" (typewriter font via class; inline style is stripped by TipTap)
  const signatoryLabel = "Signatory Name and title";
  const lastSignatoryIdx = out.lastIndexOf(signatoryLabel);
  if (lastSignatoryIdx !== -1 && signatoryInfo && (signatoryInfo.adminSignatoryName || signatoryInfo.adminSignatoryJobTitle)) {
    const parts = [signatoryInfo.adminSignatoryName, signatoryInfo.adminSignatoryJobTitle].filter(Boolean).map((s) => (s ?? "").trim());
    const line = parts.join(", ");
    if (line) {
      const insertHtml = "<p class=\"signatory-typewriter\">" + escapeHtml(line) + "</p>";
      out = out.slice(0, lastSignatoryIdx) + insertHtml + out.slice(lastSignatoryIdx);
    }
  }

  // Insert "Member Signature" right below "Member Name    Date" (last occurrence)
  const memberNameDateRe = /Member Name\s+Date/gi;
  let lastMemberNameDateMatch: RegExpExecArray | null = null;
  let memberMatch: RegExpExecArray | null;
  while ((memberMatch = memberNameDateRe.exec(out)) !== null) {
    lastMemberNameDateMatch = memberMatch;
  }
  if (lastMemberNameDateMatch) {
    const afterMatch = lastMemberNameDateMatch.index + lastMemberNameDateMatch[0].length;
    const afterStr = out.slice(afterMatch);
    const closeTag = afterStr.match(/<\/[a-z]+>/);
    if (closeTag && closeTag.index !== undefined) {
      const insertAt = afterMatch + closeTag.index + closeTag[0].length;
      out = out.slice(0, insertAt) + "<p>Member Signature</p>" + out.slice(insertAt);
    } else {
      out = out.slice(0, afterMatch) + "<p>Member Signature</p>" + out.slice(afterMatch);
    }
  }

  return out;
}

/** Format Cost column as plan_rate / plan_recurrence (e.g. "$60 / weekly"). */
const formatCost = (plan: PlanSchema): string => {
  const rateNum = plan.plan_rate;
  if (rateNum != null && plan.plan_recurrence) {
    return `$${styleThousandCommas(rateNum.toFixed(2))} / ${formatRecurrence(plan.plan_recurrence)}`;
  }
  return "N/A";
}

/** Format recurrence as "weekly", "monthly", "yearly", etc. */
const formatRecurrence = (recurrence: PlanRecurrenceOptions): string => {
  return planRecurrenceMap[recurrence];
}

/** Format Additional Hours column: "-" when overage_rate is null/undefined; otherwise rate/recurrence or N/A. */
const formatAdditionalHours = (plan: PlanSchema | ResourcePlanSchema): string => {
  const overageRate = (plan as ResourcePlanSchema & { overage_rate?: number }).overage_rate;
  if (overageRate == null || (typeof overageRate === "number" && Number.isNaN(overageRate))) {
    return "-";
  }
  // const rate = (plan as PlanSchema & { overage_rate?: number }).overage_rate;

  if (typeof overageRate === "number" && !Number.isNaN(overageRate)) {
    return `$${styleThousandCommas(overageRate.toFixed(2))} / hour`;
  }
  return "N/A";
}

/** Replaces the Exhibit A Member Subscription / Membership Plan Type table with dynamic plan data. Renames "Membership Type" -> "Membership Tier", "Cost Per Month" -> "Cost", "#of Hours" -> "# of Hours", "Corporationludes" (typo) -> "Includes". Cost cells show plan_rate / plan_recurrence; Additional Hours shows "-" when overage_rate is null. */
const applyPlansToDOLTemplate = (html: string, plans: PlanSchema[] | null | undefined): string => {
  let out = html
    .replace(/Membership Type/g, "Membership Tier")
    .replace(/Cost Per<\/strong><\/p><p><strong>Month<\/strong><\/p>/g, "Cost</strong></p>")
    .replace(/Cost Per\nMonth/g, "Cost")
    .replace(/Cost Per Month/g, "Cost")
    .replace(/#of Hours/g, "# of Hours")
    // .replace(/ludes/g, "Includes");
  if (!plans?.length) return out;
  const parser = new DOMParser();
  const doc = parser.parseFromString(out, "text/html");
  const tables = doc.querySelectorAll("table");
  let targetTable: HTMLTableElement | null = null;
  for (let i = 0; i < tables.length; i++) {
    const t = tables[i];
    const text = t.textContent ?? "";
    if ((text.includes("Membership Tier") || text.includes("Membership Type")) && (text.includes("Cost") || text.includes("#of Hours") || text.includes("# of Hours"))) {
      targetTable = t as HTMLTableElement;
      break;
    }
  }
  if (!targetTable) return out;
  const headerRow = targetTable.rows[0];
  if (headerRow) {
    for (let i = 0; i < headerRow.cells.length; i++) {
      const cell = headerRow.cells[i];
      const raw = cell.innerHTML;
      if (raw.includes("Membership Type")) cell.innerHTML = raw.replace(/Membership Type/g, "Membership Tier");
      if (raw.includes("Cost Per</strong></p><p><strong>Month</strong></p>")) cell.innerHTML = raw.replace(/<p><strong>Cost Per<\/strong><\/p><p><strong>Month<\/strong><\/p>/g, "<p><strong>Cost</strong></p>");
      else if (raw.includes("Cost Per Month")) cell.innerHTML = raw.replace(/Cost Per Month/g, "Cost");
      else if (raw.includes("Cost Per\nMonth")) cell.innerHTML = raw.replace(/Cost Per\nMonth/g, "Cost");
      if (raw.includes("#of Hours")) cell.innerHTML = raw.replace(/#of Hours/g, "# of Hours");
      if (raw.includes("Corporationludes")) cell.innerHTML = raw.replace(/ludes/g, "Includes");
    }
  }
  while (targetTable.rows.length > 1) {
    targetTable.deleteRow(1);
  }
  for (const plan of plans) {
    const tr = targetTable.insertRow();
    const addCell = (text: string) => {
      const td = tr.insertCell();
      td.innerHTML = text;
    };
    const costVal = formatCost(plan);
    const additionalVal = formatAdditionalHours(plan);
    addCell("");
    addCell(plan.plan_name ?? "");
    addCell(costVal);
    addCell(plan.plan_max_hours != null ? String(plan.plan_max_hours) : "Unlimited Access");
    addCell(Array.isArray(plan.features) ? plan.features.join(", ") : (plan.features ?? ""));
    addCell(additionalVal);
  }
  return doc.body.innerHTML;
}

/** Inserts resource-specific plan tables right under the Exhibit A (Member Subscription) table. Each selected resource gets its own table with a heading (resource name and resource type). Resources are grouped by resource_type (RESOURCE_TYPE_ORDER). Table columns: Membership Tier, Cost, # of Hours, Includes, Additional Hours. */
const applyResourcePlansToDOLTemplate = (html: string, selectedResources: ResourceSchema[] | null | undefined): string => {
  const resources = selectedResources ?? [];
  if (resources.length === 0) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const tables = doc.querySelectorAll("table");
  let exhibitATable: HTMLTableElement | null = null;
  for (let i = 0; i < tables.length; i++) {
    const t = tables[i];
    const text = t.textContent ?? "";
    if ((text.includes("Membership Tier") || text.includes("Membership Type")) && (text.includes("Cost") || text.includes("#of Hours") || text.includes("# of Hours"))) {
      exhibitATable = t as HTMLTableElement;
      break;
    }
  }
  if (!exhibitATable?.parentNode) return html;

  const byType = new Map<string, ResourceSchema[]>();
  for (const r of resources) {
    const plans = r.plans ?? [];
    if (!plans.length) continue;
    const key = (r.resource_type ?? "other").toLowerCase();
    if (!byType.has(key)) byType.set(key, []);
    byType.get(key)!.push(r);
  }
  const hasAnyResourceWithPlans = byType.size > 0;
  if (!hasAnyResourceWithPlans) return html;

  const planHeaders = ["Selected Plan (X)", "Plan", "Cost", "# of Hours", "Includes", "Additional Hours"];
  let insertBefore: ChildNode | null = exhibitATable.nextSibling;

  const sectionCaption = doc.createElement("p");
  sectionCaption.innerHTML = "<strong>Resource-specific plans included</strong>";
  sectionCaption.setAttribute("style", "margin-top: 1em;");
  exhibitATable.parentNode.insertBefore(sectionCaption, insertBefore);

  let resourceIndex = 0;
  for (const typeKey of RESOURCE_TYPE_ORDER) {
    const list = byType.get(typeKey);
    if (!list?.length) continue;
    const typeLabel = typeKey === "other" ? "Other" : typeKey.charAt(0).toUpperCase() + typeKey.slice(1);
    for (const res of list) {
      const plans = res.plans ?? [];
      if (!plans.length) continue;
      resourceIndex += 1;
      const name = res.name ?? res.id ?? "-";
      const headingText = `${resourceIndex}. ${escapeHtml(name)} (<strong>${escapeHtml(typeLabel)}</strong>):`;
      const heading = doc.createElement("p");
      heading.innerHTML = `<span>${headingText}</span>`;
      heading.setAttribute("style", "margin-top: 1em; margin-bottom: 0.25em;");
      exhibitATable.parentNode.insertBefore(heading, insertBefore);

      const table = doc.createElement("table");
      table.setAttribute("border", "1");
      table.setAttribute("cellpadding", "2");
      table.setAttribute("cellspacing", "0");
      table.setAttribute("style", "width:100%; border-collapse: collapse; margin-bottom: 0.5em;");
      const thead = table.createTHead();
      const headerRow = thead.insertRow();
      planHeaders.forEach((h) => {
        const th = headerRow.insertCell();
        th.innerHTML = `<p><strong>${h}</strong></p>`;
      });
      const tbody = table.createTBody();
      for (const plan of plans) {
        const tr = tbody.insertRow();
        tr.insertCell().innerHTML = "";
        tr.insertCell().innerHTML = plan.plan_name ?? "";
        tr.insertCell().innerHTML = formatCost(plan);
        tr.insertCell().innerHTML = plan.plan_max_hours != null ? String(plan.plan_max_hours) : "Unlimited Access";
        tr.insertCell().innerHTML = Array.isArray(plan.features) ? plan.features.join(", ") : (plan.features ?? "");
        tr.insertCell().innerHTML = formatAdditionalHours(plan);
      }
      exhibitATable.parentNode.insertBefore(table, insertBefore);
    }
  }

  return doc.body.innerHTML;
}

/** Replaces the requirements section in the DOL template with a bullet list: one "Provide a Copy of your {requirement.name}" per requirement, then "Kitchen Use Release and Waiver – provided by [business name]", then "Complete Member Orientation and Equipment Training session". */
const applyRequirementsToDOLTemplate = (
  html: string,
  requirements: RequirementSchema[] | null | undefined,
  signatoryInfo?: AgreementSignatoryInfo | null
): string => {
  const businessName = signatoryInfo?.registeredBusinessName?.trim() || "Facility Operator";
  const requirementItems = (requirements ?? [])
    .filter((r) => r.name != null && String(r.name).trim() !== "")
    .map((r) => `- Provide a Copy of your ${String(r.name).trim()}`);
  const staticItems = [
    `- Kitchen Use Release and Waiver – provided by ${businessName}`,
    "- Complete Member Orientation and Equipment Training session",
  ];
  const allItems = [...requirementItems, ...staticItems];
  // Insert only <li> elements (no wrapping <ul>) so they join the existing list and no extra space appears
  // between "Complete and sign this Agreement" and the first requirement; add newline after last item.
  const listHtml = allItems.map((item) => `<li>${item}</li>`).join("") + "\n";

  // Match the full run of requirement list items (from opening <li> through closing </li>) so replacement
  // does not leave an empty <li> or duplicate tags; template uses <li>...</li> from mammoth.
  const re = /<li>\s*Provide a [Cc]opy of your[\s\S]*?Complete Member Orientation and Equipment Training session\s*<\/li>/i;
  let out = html.replace(re, listHtml);
  out = out.replace(/<li>Complete and sign this Agreement<\/li>/i, "<li>- Complete and sign this Agreement</li>");
  return out;
}

/** Formats a single discount for display: "Name: $X off (fixed)" or "Name: X% off", optional date range and description. Returns HTML; resource name + type are wrapped in <strong> when provided. */
const formatDiscountLine = (
  d: DiscountSchema,
  resourceName?: string,
  resourceType?: string,
  escapeHtmlFn: (s: string) => string = (s) => s
): string => {
  const name = (d.name ?? "Discount").trim();
  const rate = d.rate != null ? d.rate : NaN;
  const isFixed = (d.discount_type ?? "fixed") === "fixed";
  const rateStr = isFixed
    ? `$${Number.isNaN(rate) ? "-" : rate.toFixed(2)} off`
    : `${Number.isNaN(rate) ? "-" : rate}% off`;
  const parts: string[] = [`${name}: ${rateStr}`];
  if (d.start_date || d.end_date) {
    const start = d.start_date ? moment(d.start_date).format("MMM D, YYYY") : "";
    const end = d.end_date ? moment(d.end_date).format("MMM D, YYYY") : "";
    parts.push(start && end ? `${start} - ${end}` : start || end);
  }
  if (d.description?.trim()) parts.push(d.description.trim());
  const line = parts.join(". ");
  const typeLabel = resourceType ? resourceType.charAt(0).toUpperCase() + resourceType.slice(1) : "";
  const boldPart = resourceName ? `${resourceName}${typeLabel ? ` (${typeLabel})` : ""}` : "";
  const suffix = ` - ${escapeHtmlFn(line)}`;
  return boldPart ? `<strong>${escapeHtmlFn(boldPart)}</strong>${suffix}` : escapeHtmlFn(line);
}

/** True if discount is still valid: not expired (no end_date or end_date >= today); start_date may be in the future. */
const isDiscountValid = (d: DiscountSchema): boolean => {
  const today = moment().startOf("day");
  if (d.end_date && moment(d.end_date).startOf("day").isBefore(today)) return false;
  return true;
}

/** Replaces "[List any discounts]" (and repeated placeholders with blank lines) with a bullet list of discounts from each resource in selectedResources. Only discounts that are still valid (current or future) are included. Each item shows resource name (bold), discount name, rate (fixed $ or %), date range, and optional description. Removes extra paragraph/whitespace between "Discount(s) Applied:" and the list. */
const applyDiscountsToDOLTemplate = (html: string, selectedResources: ResourceSchema[] | null | undefined): string => {
  const resources = selectedResources ?? [];
  const items: string[] = [];
  for (const res of resources) {
    const discounts = (res.discounts ?? []).filter(isDiscountValid);
    const resourceName = (res.name ?? res.id ?? "").trim();
    const label = resourceName || "Resource";
    const resourceType = res.resource_type ?? "";
    for (const d of discounts) {
      items.push(formatDiscountLine(d, label, resourceType, escapeHtml));
    }
  }
  const listHtml =
    items.length > 0
      ? "<ul>" + items.map((item) => `<li>* ${item}</li>`).join("") + "</ul>"
      : "<p><em>No discounts listed.</em></p>";
  // Replace the template's <ul><li>[List any discounts]</li>...</ul> block as a single unit so we don't nest/duplicate lists
  const templateListBlockRegex = /<ul>\s*<li>\s*\[List any discounts\]\s*<\/li>(?:\s*<li>\s*\[List any discounts\]\s*<\/li>)*\s*<\/ul>/gi;
  let out = html.replace(templateListBlockRegex, listHtml);
  // Replace block of two placeholders (with any HTML/whitespace between) by a single list, then any remaining single placeholder
  out = out.replace(/\[List any discounts\][\s\S]*?\[List any discounts\]/g, listHtml);
  out = out.replace(/\[List any discounts\]/g, listHtml);
  // Remove extra paragraph/whitespace between "Discount(s) Applied:" and the list so there is no gap
  out = out.replace(
    /(Discount\(s\) Applied:)\s*<\/p>\s*(?:<p[^>]*>\s*)?<ul>/gi,
    "$1</p><ul>"
  );
  return out;
}

/** Returns HTML for a centered logo block (250×100 px max, aspect ratio preserved) at the top of the document. */
const logoBlockHtml = (logoUrl: string): string => {
  const src = logoUrl.trim();
  if (!src) return "";
  return `<p class="agreement-logo-block" style="text-align: center; margin: 0 0 1em 0;"><img src="${src}" alt="Community logo" style="max-width: 250px; max-height: 100px; width: auto; height: auto; object-fit: contain; display: inline-block; vertical-align: middle;" /></p>`;
}

/** Injects the community logo at the top of the document so it appears on the first page (upload, template, or blank). */
const injectLogoAtTop = (html: string, logoUrl: string | undefined): string => {
  const block = logoUrl ? logoBlockHtml(logoUrl) : "";
  if (!block) return html;
  return block + html;
}

/** Replaces "Security Deposit (refundable): [$xxx]" (or similar placeholder) with the formatted security deposit amount. */
const applySecurityDepositToDOLTemplate = (html: string, securityDeposit: number): string => {
  // const value = Number(securityDeposit);
  const formatted = (securityDeposit === 0 || Number.isNaN(securityDeposit)) ? "[$xxx]" : `$${styleThousandCommas(securityDeposit.toFixed(2))}`;
  return html.replace(/Security Deposit \(refundable\):\s*\[\$xxx\]/gi, `Security Deposit (refundable): ${formatted}`);
}

/** Fetches DOL template from DOCX, strips embedded images, replaces org name/address/signatory with community and signatoryInfo, replaces Exhibit A plan table with plans, inserts resource plans table from selectedResources, replaces "[List any discounts]" with bullet list from resource.discounts, replaces requirements section with bullet list from requirements, replaces Security Deposit (refundable): [$xxx] with securityDeposit; logo is injected via injectLogoAtTop. */
const fetchDOLTemplateHtml = async (
  community: CommunityForTemplate | null | undefined,
  signatoryInfo?: AgreementSignatoryInfo | null,
  plans?: PlanSchema[] | null,
  requirements?: RequirementSchema[] | null,
  selectedResources?: ResourceSchema[] | null,
  securityDeposit?: number
): Promise<string> => {
  const res = await fetch(DOL_TEMPLATE_URL);
  if (!res.ok) throw new Error("Could not load DOL template. Ensure DOL Member Agreement.docx is in public/templates/.");
  const arrayBuffer = await res.arrayBuffer();
  const result = await mammoth.convertToHtml({ arrayBuffer });
  let html = result.value;
  html = stripImagesFromHtml(html);
  html = applyCommunityToDOLTemplate(html, community, signatoryInfo);
  html = applyPlansToDOLTemplate(html, plans ?? null);
  html = applyResourcePlansToDOLTemplate(html, selectedResources ?? null);
  html = applyDiscountsToDOLTemplate(html, selectedResources ?? null);
  html = applyRequirementsToDOLTemplate(html, requirements ?? null, signatoryInfo);
  html = applySecurityDepositToDOLTemplate(html, securityDeposit ?? 0);
  return html;
}

const BLANK_TEMPLATE_HTML = "<p>Start writing your member agreement here. Use the toolbar above to format text, add headings, and create lists.</p>";

const TEMPLATES = [
  {
    id: "dol",
    name: "Membership Agreement Template",
    description: "Kitchen/shared facility member agreement template. Uses your community logo.",
  },
  {
    id: "blank",
    name: "Blank document with logo",
    description: "Start from scratch with an empty editable document.",
    html: BLANK_TEMPLATE_HTML,
  },
] as const;

const TEMPLATE_SETUP_ICONS: Record<string, { iconSrc: string; activeIconSrc: string }> = {
  dol: {
    iconSrc: "/images/memberships/library-outline.svg",
    activeIconSrc: "/images/memberships/library-bold.svg",
  },
  blank: {
    iconSrc: "/images/memberships/document-add-outline.svg",
    activeIconSrc: "/images/memberships/document-add-bold.svg",
  },
  saved: {
    iconSrc: "/images/memberships/document-text-outline.svg",
    activeIconSrc: "/images/memberships/document-text-bold.svg",
  },
};

const parseFileToHtml = async (file: File): Promise<string> => {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  const type = file.type;

  if (
    ext === "docx" ||
    ext === "doc" ||
    type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    type === "application/msword"
  ) {
    const buf = await file.arrayBuffer();
    const result = await mammoth.convertToHtml({ arrayBuffer: buf });
    return result.value;
  }

  if (ext === "md" || type === "text/markdown") {
    const text = await file.text();
    return marked.parse(text) as string;
  }

  if (ext === "html" || ext === "htm" || type === "text/html") {
    return file.text();
  }

  if (ext === "txt" || type === "text/plain") {
    const text = await file.text();
    return "<p>" + text.replace(/\n/g, "</p><p>") + "</p>";
  }

  if (ext === "rtf" || type === "application/rtf") {
    const text = await file.text();
    return "<p>RTF content (paste or use DOCX for best results):</p><pre>" + escapeHtml(text.slice(0, 5000)) + "</pre>";
  }

  if (ext === "pdf" || type === "application/pdf") {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const numPages = pdf.numPages;
      const textParts: string[] = [];
      for (let i = 1; i <= numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item) => ("str" in item ? item.str : ""))
          .join(" ");
        if (pageText.trim()) textParts.push(pageText);
      }
      const fullText = textParts.join("\n\n").trim();
      if (!fullText) {
        return "<p><em>No text could be extracted from this PDF (it may be image-based or empty). Try uploading a DOCX or copy-pasting the text after \"Start from scratch\".</em></p>";
      }
      return "<p>" + fullText.replace(/\n\n+/g, "</p><p>").replace(/\n/g, " ") + "</p>";
    } catch (err) {
      const msg = err instanceof Error ? err.message : "PDF could not be read.";
      throw new Error(`PDF extraction failed: ${msg}`);
    }
  }

  return "<p>Unsupported format. Please use DOCX, PDF (see note), MD, HTML, or TXT.</p>";
}

const escapeHtml = (s: string): string => {
  const div = document.createElement("div");
  div.textContent = s;
  return div.innerHTML;
}

const IMAGE_ACCEPT = "image/png,image/jpeg,image/gif,image/webp";

const AGREEMENT_EDITOR_STYLES =
  ".agreement-editor-inner p.signatory-typewriter { font-family: 'Courier New', Courier, monospace; }" +
  ".agreement-editor-inner .agreement-logo-block { text-align: center; }" +
  ".agreement-editor-inner .agreement-logo-block img { max-width: 250px; max-height: 100px; width: auto; height: auto; object-fit: contain; }" +
  ".agreement-editor-inner p.agreement-logo-block + img { max-width: 250px !important; max-height: 100px !important; width: auto !important; height: auto !important; object-fit: contain; display: block; margin-left: auto; margin-right: auto; }";

const AGREEMENT_EDITOR_INNER_CLASS =
  "agreement-editor-inner min-h-[720px] w-full bg-transparent px-0 py-0 focus:outline-none font-open-sans text-[14px] leading-[1.68] text-[#4D4D4D] " +
  "[&_h1]:font-raleway [&_h1]:text-[24px] [&_h1]:font-bold [&_h1]:leading-[1.2] [&_h1]:text-[#111111] [&_h1]:mt-8 [&_h1:first-child]:mt-0 " +
  "[&_h2]:font-raleway [&_h2]:text-xl [&_h2]:font-bold [&_h2]:leading-[1.2] [&_h2]:text-[#111111] [&_h2]:mt-8 [&_h2:first-child]:mt-0 " +
  "[&_h3]:font-raleway [&_h3]:text-lg [&_h3]:font-bold [&_h3]:leading-[1.2] [&_h3]:text-[#111111] [&_h3]:mt-6 [&_h3:first-child]:mt-0 " +
  "[&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5 [&_blockquote]:border-l-4 [&_blockquote]:border-primary-200 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-[#555] " +
  "[&_table]:border-collapse [&_table]:w-full [&_th]:border [&_th]:border-[#ccc] [&_th]:p-2 [&_th]:bg-[#f5f5f5] [&_td]:border [&_td]:border-[#ccc] [&_td]:p-2 " +
  "[&_img]:max-w-full [&_img]:h-auto [&_p:has(>img)]:text-center [&_p:has(>img)]:my-4";

const EditorSidebarToolButton = ({
  label,
  iconSrc,
  active = false,
  onClick,
  title,
}: {
  label: string;
  iconSrc?: string;
  active?: boolean;
  onClick: () => void;
  title?: string;
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title ?? label}
      aria-label={label}
      className={`group flex h-20 flex-col items-center justify-center gap-2 rounded-lg border bg-white px-2 font-open-sans text-[11px] font-semibold transition-colors ${
        active
          ? "border-primary-200 bg-primary-200 text-white"
          : "border-[#E9E9E9]/70 text-[#808080] hover:border-primary-200 hover:bg-primary-200 hover:text-white"
      }`}
    >
      {iconSrc ? (
        <img
          src={iconSrc}
          alt=""
          className={`h-6 w-6 transition-[filter] ${active ? "brightness-0 invert" : "group-hover:brightness-0 group-hover:invert"}`}
          aria-hidden
        />
      ) : null}
      {label}
    </button>
  );
};

const EditorSidebarIconButton = ({
  label,
  active = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: ReactNode;
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`flex h-10 items-center justify-center rounded-lg border transition-colors ${
        active
          ? "border-primary-200 bg-primary-200 text-white"
          : "border-[#E9E9E9]/70 bg-white text-[#737373] hover:border-primary-200 hover:bg-primary-200 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
};

const AgreementEditorSidebar = ({
  editor,
  onInsertImage,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
  onInsertImage: () => void;
}) => {
  return (
    <aside className="border-r border-[#E9E9E9]/70 bg-white pt-8">
      <div className="px-4">
        <p className="font-raleway text-[14px] font-semibold text-[#808080]">Formatting</p>
        <div className="mt-4 grid grid-cols-4 gap-2">
          <EditorSidebarIconButton
            label="Bold"
            active={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FormatBoldIcon sx={{ fontSize: 18 }} />
          </EditorSidebarIconButton>
          <EditorSidebarIconButton
            label="Italic"
            active={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FormatItalicIcon sx={{ fontSize: 18 }} />
          </EditorSidebarIconButton>
          <EditorSidebarIconButton
            label="Underline"
            active={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <FormatUnderlinedIcon sx={{ fontSize: 18 }} />
          </EditorSidebarIconButton>
          <EditorSidebarIconButton
            label="Strikethrough"
            active={editor.isActive("strike")}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <FormatStrikethroughIcon sx={{ fontSize: 18 }} />
          </EditorSidebarIconButton>
        </div>

        <p className="mt-7 font-raleway text-[14px] font-semibold text-[#808080]">Content Blocks</p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <EditorSidebarToolButton
            label="Heading"
            iconSrc="/images/memberships/builder-heading.svg"
            active={editor.isActive("heading")}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          />
          <EditorSidebarToolButton
            label="Text"
            iconSrc="/images/memberships/builder-text.svg"
            active={editor.isActive("paragraph")}
            onClick={() => editor.chain().focus().setParagraph().run()}
          />
          <EditorSidebarToolButton
            label="Image"
            iconSrc="/images/memberships/builder-image.svg"
            onClick={onInsertImage}
          />
          <EditorSidebarToolButton
            label="List"
            iconSrc="/images/memberships/builder-list.svg"
            active={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          />
          <EditorSidebarToolButton
            label="Numbered"
            iconSrc="/images/memberships/builder-list.svg"
            active={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          />
          <EditorSidebarToolButton
            label="Table"
            iconSrc="/images/memberships/solar_document-text-linear.svg"
            onClick={() => {
              editor.chain().focus().run();
              (
                editor.commands as {
                  insertTable?: (o?: { rows?: number; cols?: number; withHeaderRow?: boolean }) => boolean;
                }
              ).insertTable?.({ rows: 3, cols: 3, withHeaderRow: true });
            }}
          />
          <EditorSidebarToolButton
            label="Quote"
            iconSrc="/images/memberships/builder-text.svg"
            active={editor.isActive("blockquote")}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          />
        </div>

        <p className="mt-7 font-raleway text-[14px] font-semibold text-[#808080]">Heading Level</p>
        <div className="mt-4 grid grid-cols-3 gap-2">
          <EditorSidebarIconButton
            label="Heading 1"
            active={editor.isActive("heading", { level: 1 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          >
            <TitleIcon sx={{ fontSize: 16 }} />
          </EditorSidebarIconButton>
          <EditorSidebarIconButton
            label="Heading 2"
            active={editor.isActive("heading", { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <TitleIcon sx={{ fontSize: 15 }} />
          </EditorSidebarIconButton>
          <EditorSidebarIconButton
            label="Heading 3"
            active={editor.isActive("heading", { level: 3 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            <TitleIcon sx={{ fontSize: 14 }} />
          </EditorSidebarIconButton>
        </div>
      </div>
    </aside>
  );
}

const AgreementEditorDocumentPanel = ({
  pageTitle,
  onPageTitleChange,
}: {
  pageTitle: string;
  onPageTitleChange: (title: string) => void;
}) => {
  return (
    <aside className="border-l border-[#E9E9E9]/70 bg-white px-4 py-8">
      <div className="flex items-center justify-between">
        <p className="font-raleway text-[14px] font-bold text-primary-200">Pages</p>
      </div>

      <div className="mt-7 space-y-2">
        <label className="block">
          <span className="font-open-sans text-[11px] font-semibold uppercase tracking-wide text-[#737373]">
            Page title
          </span>
          <input
            value={pageTitle}
            onChange={(event) => onPageTitleChange(event.target.value)}
            className="mt-2 h-10 w-full rounded-lg border border-[#E9E9E9]/70 px-3 font-open-sans text-[14px] text-primary-200 outline-none focus:border-primary-200"
            aria-label="Page title"
          />
        </label>
      </div>

      <div className="mt-7 space-y-3">
        <div className="rounded-xl border border-primary-200 bg-white p-2 shadow-sm">
          <div className="flex h-12 w-full items-center gap-3 px-2 text-left">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded border border-[#E9E9E9]/70 bg-white font-raleway text-[14px] font-semibold text-primary-200">
              1
            </span>
            <span className="min-w-0 flex-1 truncate font-raleway text-[14px] font-semibold text-primary-200">
              {pageTitle.trim() || "Page 1"}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-8 rounded-xl bg-[#f8f8f8] px-4 py-4">
        <p className="font-raleway text-[13px] font-semibold text-primary-200">Editing tips</p>
        <ul className="mt-3 space-y-2 font-open-sans text-[12px] leading-relaxed text-[#666666]">
          <li>Use content blocks on the left to format headings, lists, and tables.</li>
          <li>Your document is saved as one continuous agreement page.</li>
          <li>Imported logos and signatory lines keep their special styling.</li>
        </ul>
      </div>
    </aside>
  );
}

const AgreementEditor = ({
  initialHtml,
  onContentChange,
  contractTitle,
}: {
  initialHtml: string;
  onContentChange?: (html: string) => void;
  contractTitle?: string;
}) => {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const savedSelectionRef = useRef<{ anchor: number; head: number } | null>(null);
  const [pageTitle, setPageTitle] = useState(contractTitle?.trim() || "Page 1");

  useEffect(() => {
    if (contractTitle?.trim()) {
      setPageTitle(contractTitle.trim());
    }
  }, [contractTitle]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, paragraph: false }),
      ParagraphWithClass,
      TableKit.configure({ table: { resizable: true } }),
      Image.configure({ inline: false, allowBase64: true }),
    ],
    content: initialHtml,
    editorProps: {
      attributes: {
        class: AGREEMENT_EDITOR_INNER_CLASS,
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      onContentChange?.(currentEditor.getHTML());
    },
  });

  const handleInsertImage = useCallback(() => {
    if (!editor) return;
    const { anchor, head } = editor.state.selection;
    savedSelectionRef.current = { anchor, head };
    imageInputRef.current?.click();
  }, [editor]);

  if (!editor) return null;

  return (
    <Tiptap instance={editor}>
      <style>{AGREEMENT_EDITOR_STYLES}</style>
      <main className="grid min-h-[760px] grid-cols-1 bg-[#f8f8f8] lg:grid-cols-[210px_1fr_240px]">
        <AgreementEditorSidebar editor={editor} onInsertImage={handleInsertImage} />

        <section className="relative overflow-auto px-6 py-8 lg:px-10">
          <div className="mx-auto max-w-[760px]">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-raleway text-[14px] font-semibold text-primary-200">Page 1</span>
            </div>
            <article className="min-h-[920px] border border-primary-200 bg-white px-8 py-12 shadow-sm sm:px-14 sm:py-16">
              <TiptapContent className="agreement-editor-content min-h-[720px]" />
            </article>
          </div>
        </section>

        <AgreementEditorDocumentPanel pageTitle={pageTitle} onPageTitleChange={setPageTitle} />

        <input
          ref={imageInputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file || !editor) return;
            const saved = savedSelectionRef.current;
            savedSelectionRef.current = null;
            const reader = new FileReader();
            reader.onload = () => {
              const src = reader.result as string;
              if (!src) return;
              const chain = editor.chain().focus();
              if (saved != null) {
                try {
                  chain.setTextSelection({ from: saved.anchor, to: saved.head });
                } catch {
                  // positions may be invalid if doc changed
                }
              }
              chain.setImage({ src }).run();
            };
            reader.readAsDataURL(file);
          }}
        />
      </main>
    </Tiptap>
  );
}

type UploadStatus = "idle" | "parsing" | "success" | "error";

type ViewMode = "dashboard" | "setup" | "editor";

type SetupStep = "options" | "templates";

type AgreementStatus = "draft" | "sent" | "viewed" | "signed" | "expired";

type AdminSignatoryDetails = {
  registeredBusinessName: string;
  registeredBusinessEntity: string;
  adminSignatoryName: string;
  adminSignatoryJobTitle: string;
};

type AgreementRow = {
  id: string;
  title: string;
  memberName: string;
  tier: string;
  status: AgreementStatus;
  lastUpdated: string;
  signatures: { required: number; completed: number };
  updatedAtMs?: number;
  isCommunityDraft?: boolean;
  adminSignatory?: AdminSignatoryDetails;
};

const AGREEMENT_HEADER_CELL =
  "px-5 py-3 text-left text-[11px] font-raleway font-semibold uppercase tracking-wide text-[#999999]";

const formatAdminBusinessDisplay = (
  info: Pick<AdminSignatoryDetails, "registeredBusinessName" | "registeredBusinessEntity">
): string => {
  const name = info.registeredBusinessName?.trim();
  const entity = info.registeredBusinessEntity?.trim();
  if (name && entity) return `${name} (${entity})`;
  return name || entity || "-";
}

type MemberAgreementProps = {
  communityId?: string;
};

const AGREEMENT_STATS = [
  { label: "Total memberships", detail: "One agreement per member" },
  { label: "Awaiting signatures", detail: "Sent but not completed" },
  { label: "Completed signatures", detail: "Members who submitted their agreement" },
  { label: "Expiring soon", detail: "Need review this month" },
] as const;

// const SIDEBAR_TEMPLATES = [
//   {
//     name: "Kitchen Access Agreement",
//     description: "Standard terms for members booking shared kitchen time.",
//   },
//   {
//     name: "Storage & Inventory Agreement",
//     description: "Cold storage, shelf space, and inventory handling terms.",
//   },
//   {
//     name: "Training Program Agreement",
//     description: "Membership terms for culinary training and mentoring cohorts.",
//   },
// ] as const;

const STATUS_STYLES: Record<AgreementStatus, string> = {
  signed: "bg-emerald-50 text-emerald-700",
  viewed: "bg-orange-50 text-orange-700",
  sent: "bg-sky-50 text-sky-700",
  draft: "bg-[#F0F0F0] text-[#666666]",
  expired: "bg-red-50 text-red-600",
};

type BUSINESS_ENTITY_TYPES = "LLC" | "Inc" | "Corporation" | "Nonprofit" | "Sole Proprietorship" | "Partnership" | "Other";
const BUSINESS_ENTITY_OPTIONS: BUSINESS_ENTITY_TYPES[] = ["LLC", "Inc", "Corporation", "Nonprofit", "Sole Proprietorship", "Partnership", "Other"];

/** Display order for resource_type; types not listed appear after "Other". */
const RESOURCE_TYPE_ORDER = ["kitchen", "documentation", "training", "funding", "expert", "farm", "other"] as const;

const getCommunityAgreementTitle = (communityName?: string | null, savedAgreementName?: string | null): string => {
  const savedName = savedAgreementName?.trim();
  if (savedName) return savedName;
  const community = communityName?.trim();
  return `${community || "Community"} Member Agreement`;
}

const resolveReminderGreetingName = (memberName: string): string => {
  const trimmed = memberName.trim();
  if (!trimmed || trimmed.includes("@")) return "there";
  return trimmed.split(/\s+/)[0] || "there";
};

const resolveAbsoluteFileUrl = (fileUrl?: string): string | undefined => {
  const trimmed = fileUrl?.trim();
  if (!trimmed) return undefined;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return undefined;
};

const buildAgreementReminderMessage = (
  memberName: string,
  agreementTitle: string,
  communityName?: string | null
): string => {
  const greetingName = resolveReminderGreetingName(memberName);
  const title = agreementTitle.trim() || "Member Agreement";
  const community = communityName?.trim() || "Community";

  return `Hi ${greetingName}, just a reminder that your ${title} is ready for review. Please sign it when you can, and reply here if you have any questions. Thanks, ${community} Team`;
}

const formatMemberDisplayName = (member: MembershipSchema): string => {
  const first = member.first_name?.trim();
  const last = member.last_name?.trim();
  if (first || last) return [first, last].filter(Boolean).join(" ");
  if (member.username?.trim()) return member.username.trim();
  if (member.email?.trim()) return member.email.trim();
  return "Unknown member";
}

const formatMemberTier = (member: MembershipSchema): string => {
  if (member.plan_rate != null && member.plan_recurrence) {
    return `$${styleThousandCommas(Number(member.plan_rate).toFixed(0))}/${formatRecurrence(member.plan_recurrence)}`;
  }
  if (member.plan_name?.trim()) return member.plan_name.trim();
  return "-";
}

const resolveMemberAgreementStatus = (member: MembershipSchema): AgreementStatus => {
  const submitted = member.member_agreement?.submitted;
  if (submitted && moment(submitted).isValid()) return "signed";
  if (member.member_agreement?.file_url) return "viewed";
  return "sent";
}

const resolveMemberAgreementUpdatedAt = (member: MembershipSchema): string | undefined => {
  return member.member_agreement?.submitted || member.last_updated || member.joined;
}

const formatAgreementRelativeTime = (dateValue: string | undefined, timezone: string): string => {
  if (!dateValue) return "-";
  const parsed = moment.utc(dateValue).tz(timezone);
  return parsed.isValid() ? parsed.fromNow() : "-";
}

const resolveMemberSignatures = (member: MembershipSchema): { required: number; completed: number } => {
  const required = 2;
  if (member.member_agreement?.submitted) return { required, completed: 1 };
  if (member.member_agreement?.signature_data_url) return { required, completed: required };
  return { required, completed: 0 };
}

const memberAgreementRowId = (member: MembershipSchema): string => {
  if (member.uid?.trim()) return member.uid.trim();
  if (member.username?.trim()) return member.username.trim();
  if (member.id?.trim()) return member.id.trim();
  if (member.email?.trim()) return member.email.trim();
  return "member";
}

const consolidateMembershipRecords = (
  memberships: MembershipSchema[] | null | undefined,
  membershipRequests: MembershipSchema[] | null | undefined
): { member: MembershipSchema; isRequest: boolean }[] => {
  const seen = new Set<string>();
  const consolidated: { member: MembershipSchema; isRequest: boolean }[] = [];

  const append = (member: MembershipSchema, isRequest: boolean) => {
    const key = memberAgreementRowId(member);
    if (seen.has(key)) return;
    seen.add(key);
    consolidated.push({ member, isRequest });
  };

  for (const member of memberships ?? []) append(member, false);
  for (const member of membershipRequests ?? []) append(member, true);
  return consolidated;
}

const buildAgreementRowsFromMembers = (
  members: { member: MembershipSchema; isRequest: boolean }[],
  agreementTitle: string,
  timezone: string
): AgreementRow[] => {
  return members.map(({ member }) => {
    const updatedAt = resolveMemberAgreementUpdatedAt(member);
    return {
      id: memberAgreementRowId(member),
      title: agreementTitle,
      memberName: formatMemberDisplayName(member),
      tier: formatMemberTier(member),
      status: resolveMemberAgreementStatus(member),
      lastUpdated: formatAgreementRelativeTime(updatedAt, timezone),
      signatures: resolveMemberSignatures(member),
      updatedAtMs: updatedAt ? moment.utc(updatedAt).valueOf() : 0,
    };
  });
}

type SignatureHistoryItem = { id: string; event: string; meta: string; sortAt: number };

const buildSignatureHistory = (
  members: { member: MembershipSchema; isRequest: boolean }[],
  agreementTitle: string,
  timezone: string
): { id: string; event: string; meta: string }[] => {
  const events: SignatureHistoryItem[] = [];

  for (const { member, isRequest } of members) {
    const name = formatMemberDisplayName(member);
    const rowId = memberAgreementRowId(member);
    const submitted = member.member_agreement?.submitted;
    const updatedAt = resolveMemberAgreementUpdatedAt(member);

    if (submitted && moment(submitted).isValid()) {
      const event = `${name} signed ${agreementTitle}`;
      const sortAt = moment.utc(submitted).valueOf();
      events.push({
        id: `${rowId}-${sortAt}`,
        event,
        meta: `Completed via resource pantry · ${formatAgreementRelativeTime(submitted, timezone)}`,
        sortAt,
      });
      continue;
    }

    if (member.member_agreement?.file_url) {
      const event = `${name} viewed ${agreementTitle}`;
      const sortAt = updatedAt ? moment.utc(updatedAt).valueOf() : 0;
      events.push({
        id: `${rowId}-${sortAt}`,
        event,
        meta: `Opened from resource pantry · ${formatAgreementRelativeTime(updatedAt, timezone)}`,
        sortAt,
      });
      continue;
    }

    if (isRequest) {
      const event = `${name} was sent ${agreementTitle}`;
      const sortAt = updatedAt ? moment.utc(updatedAt).valueOf() : 0;
      events.push({
        id: `${rowId}-${sortAt}`,
        event,
        meta: `Pending membership request · ${formatAgreementRelativeTime(updatedAt, timezone)}`,
        sortAt,
      });
    }
  }

  return events
    .sort((a, b) => b.sortAt - a.sortAt)
    .slice(0, 4)
    .map(({ id, event, meta }) => ({ id, event, meta }));
}

const groupResourcesByType = (resources: ResourceSchema[]): { typeLabel: string; typeKey: string; resources: ResourceSchema[] }[] => {
  const byType = new Map<string, ResourceSchema[]>();
  for (const res of resources) {
    const key = (res.resource_type ?? "other").toLowerCase();
    if (!byType.has(key)) byType.set(key, []);
    byType.get(key)!.push(res);
  }
  const ordered: { typeLabel: string; typeKey: string; resources: ResourceSchema[] }[] = [];
  for (const key of RESOURCE_TYPE_ORDER) {
    const list = byType.get(key);
    if (list?.length) {
      ordered.push({
        typeKey: key,
        typeLabel: key === "other" ? "Other" : key.charAt(0).toUpperCase() + key.slice(1),
        resources: list,
      });
    }
  }
  byType.forEach((list, key) => {
    if (!RESOURCE_TYPE_ORDER.includes(key as typeof RESOURCE_TYPE_ORDER[number])) {
      ordered.push({
        typeKey: key,
        typeLabel: key.charAt(0).toUpperCase() + key.slice(1),
        resources: list,
      });
    }
  });
  return ordered;
}

const ComposerField = ({
  label,
  value,
  onChange,
  readOnly = false,
  id,
  type = "text",
  placeholder,
  min,
  step,
}: {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  id?: string;
  type?: string;
  placeholder?: string;
  min?: number;
  step?: number;
}) => {
  return (
    <label htmlFor={id} className="flex flex-col gap-2">
      <span className="text-[11px] font-raleway font-semibold uppercase tracking-[0.14em] text-primary-300">
        {label}
      </span>
      <input
        id={id}
        type={type}
        value={value}
        readOnly={readOnly}
        placeholder={placeholder}
        min={min}
        step={step}
        onChange={readOnly ? undefined : (e) => onChange?.(e.target.value)}
        className="h-11 w-full rounded-lg border border-[#E9E9E9] bg-white px-3 text-[14px] font-open-sans text-[#333333] focus:border-primary-200 focus:outline-none focus:ring-1 focus:ring-primary-200 read-only:bg-[#FAFAFA] read-only:text-[#666666]"
      />
    </label>
  );
}

const ComposerSelectField = ({
  label,
  id,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[];
  placeholder?: string;
}) => {
  return (
    <label htmlFor={id} className="flex flex-col gap-2">
      <span className="text-[11px] font-raleway font-semibold uppercase tracking-[0.14em] text-primary-300">
        {label}
      </span>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 w-full rounded-lg border border-[#E9E9E9] bg-white px-3 text-[14px] font-open-sans text-[#333333] focus:border-primary-200 focus:outline-none focus:ring-1 focus:ring-primary-200"
      >
        <option value="">{placeholder ?? "Select an option"}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
}

const AgreementSignatorySettingsFields = ({
  signatoryInfo,
  securityDeposit,
  resourcesOffered,
  onSignatoryChange,
  onSecurityDepositChange,
  onToggleResourceSelection,
}: {
  signatoryInfo: AgreementSignatoryInfo;
  securityDeposit: number;
  resourcesOffered: ResourceSchema[];
  onSignatoryChange: (field: keyof AgreementSignatoryInfo, value: string) => void;
  onSecurityDepositChange: (value: number) => void;
  onToggleResourceSelection: (resourceId: string) => void;
}) => {
  return (
    <>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <ComposerField
          id="registered-business-name"
          label="Registered Business Name"
          value={signatoryInfo.registeredBusinessName ?? ""}
          onChange={(value) => onSignatoryChange("registeredBusinessName", value)}
          placeholder="e.g. Acme Kitchen LLC"
        />
        <ComposerSelectField
          id="registered-business-entity"
          label="Registered Business Entity"
          value={signatoryInfo.registeredBusinessEntity ?? ""}
          onChange={(value) => onSignatoryChange("registeredBusinessEntity", value)}
          options={BUSINESS_ENTITY_OPTIONS}
          placeholder="Select entity type"
        />
        <ComposerField
          id="admin-signatory-name"
          label="Signatory Full Name"
          value={signatoryInfo.adminSignatoryName ?? ""}
          onChange={(value) => onSignatoryChange("adminSignatoryName", value)}
          placeholder="Full name of signatory"
        />
        <ComposerField
          id="admin-signatory-title"
          label="Signatory Job Title"
          value={signatoryInfo.adminSignatoryJobTitle ?? ""}
          onChange={(value) => onSignatoryChange("adminSignatoryJobTitle", value)}
          placeholder="e.g. Community Director"
        />
        <ComposerField
          id="security-deposit"
          label="Security Deposit ($)"
          type="number"
          value={securityDeposit ? String(securityDeposit) : ""}
          onChange={(value) => onSecurityDepositChange(value === "" ? 0 : Number(value))}
          placeholder="Enter amount"
          min={0}
          step={0.01}
        />
      </div>

      <div className="mt-6 rounded-xl border border-[#E9E9E9]/70 bg-white/80 p-5">
        <p className="text-[11px] font-raleway font-semibold uppercase tracking-[0.14em] text-primary-300">
          Resources covered
        </p>
        {resourcesOffered.length === 0 ? (
          <p className="mt-3 text-[14px] font-open-sans text-[#666666]">
            No specific culinary resources have been selected. This agreement is strictly for community membership
            access and does not offer resource usage.
          </p>
        ) : (
          <div className="mt-3 flex flex-col gap-4">
            <p className="text-[14px] font-open-sans text-[#666666]">
              Select one or more resources that this member agreement covers.
            </p>
            <div className="grid grid-cols-1 items-start gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
              {groupResourcesByType(resourcesOffered).map(({ typeLabel, typeKey, resources: list }) => (
                <div key={typeKey} className="flex min-w-0 flex-col gap-2">
                  <p className="text-[11px] font-raleway font-semibold uppercase tracking-wide text-[#808080]">
                    {typeLabel}s
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {list.map((res) => {
                      const id = res.id ?? "";
                      const checked = signatoryInfo.selectedResourceIds?.includes(id) ?? false;
                      return (
                        <label
                          key={id}
                          className="flex cursor-pointer items-center gap-2 text-[14px] font-open-sans text-[#333333]"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => onToggleResourceSelection(id)}
                            className="shrink-0 rounded border-[#ccc] text-primary-200 focus:ring-primary-200"
                          />
                          {res.name || res.id || "Unnamed resource"}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const AgreementSetupOption = ({
  iconSrc,
  activeIconSrc,
  title,
  description,
  onClick,
  disabled = false,
  meta,
  actionLabel = "Get started",
  actionAriaLabel,
}: {
  iconSrc: string;
  activeIconSrc: string;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
  meta?: string;
  actionLabel?: string;
  actionAriaLabel?: string;
}) => {
  return (
    <article className="group flex min-h-[258px] flex-col rounded-2xl bg-white p-7 text-left transition-shadow hover:shadow-md">
      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#f8f8f8] text-primary-200 transition-colors group-hover:bg-primary-200 group-hover:text-white">
        <MaskedIcon src={iconSrc} className="h-6 w-6 bg-primary-200 group-hover:hidden" />
        <MaskedIcon src={activeIconSrc} className="hidden h-6 w-6 bg-white group-hover:block" />
      </span>
      <h4 className="mt-6 font-raleway text-[18px] font-bold text-primary-200">{title}</h4>
      <p className="mt-3 text-[14px] font-open-sans text-[#4D4D4D]">{description}</p>
      {meta ? <p className="mt-3 text-[12px] font-open-sans text-[#808080]">{meta}</p> : null}
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={actionAriaLabel ?? title}
        className="mt-8 inline-flex h-12 w-full items-center justify-between rounded-lg border border-[#E9E9E9] bg-white pl-4 pr-4 font-raleway text-[14px] font-semibold text-primary-200 transition-colors hover:border-primary-200 hover:bg-primary-200 hover:text-white disabled:cursor-wait disabled:opacity-60"
      >
        <span>{actionLabel}</span>
        <ArrowForwardIcon sx={{ fontSize: 16 }} />
      </button>
    </article>
  );
}

const MemberAgreement = ({ communityId = "" }: MemberAgreementProps) => {
  const { primaryCommunity } = useLoadCommunity(communityId);
  const { memberships } = useLoadMemberships();
  const { membershipRequests } = useLoadRequests();
  const requirements = useMemo(() => primaryCommunity?.requirements ?? [], [primaryCommunity?.requirements]);
  const plans = useMemo(() => primaryCommunity?.plans ?? [], [primaryCommunity?.plans]);
  const { agreement: savedAgreement, agreementLoading: savedAgreementLoading, addMemberAgreementData: addMemberAgreement, reloadAgreement } = useGetMemberAgreement(communityId);
  const communityTimezone = primaryCommunity?.timezone || "America/New_York";
  const communityAgreementTitle = useMemo(
    () => getCommunityAgreementTitle(primaryCommunity?.name, savedAgreement?.name),
    [primaryCommunity?.name, savedAgreement?.name]
  );
  const consolidatedMembers = useMemo(
    () => consolidateMembershipRecords(memberships, membershipRequests),
    [memberships, membershipRequests]
  );
  const { sendMessageAsync } = useSendMessage();
  const [mode, setMode] = useState<ViewMode>("dashboard");
  const [setupStep, setSetupStep] = useState<SetupStep>("options");
  const [activeRowMenu, setActiveRowMenu] = useState<string | null>(null);
  const [sendingReminderUid, setSendingReminderUid] = useState<string | null>(null);
  const [memberAgreementPreview, setMemberAgreementPreview] = useState<CurrentRequirementsSchema | null>(null);
  const [viewMemberDetails, setViewMemberDetails] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MembershipSchema | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [agreementSortField, setAgreementSortField] = useState<AgreementSortField>("updated");
  const [agreementSortDirection, setAgreementSortDirection] = useState<SortDirection>("desc");
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [editorHtml, setEditorHtml] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lastFileName, setLastFileName] = useState<string | null>(null);
  const [dolLoading, setDolLoading] = useState(false);
  const [saveDraftLoading, setSaveDraftLoading] = useState(false);

  // console.log("savedAgreement", savedAgreement);
  // const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const [signatoryInfo, setSignatoryInfo] = useState<AgreementSignatoryInfo>({
    registeredBusinessName: primaryCommunity?.name ?? "",
    registeredBusinessEntity: "",
    adminSignatoryName: "",
    adminSignatoryJobTitle: "",
    selectedResourceIds: [],
  });

  const { resources, isErrorResources, error } = useLoadCommunityResources();
  const resourcesOffered = resources || [];
  const selectedResources = resourcesOffered.filter((r) => r.id && (signatoryInfo.selectedResourceIds?.includes(r.id) ?? false));

  useEffect(() => {
    setSecurityDeposit(savedAgreement?.security_deposit ?? 0);
    if (savedAgreement?.signatory_info) {
      setSignatoryInfo(savedAgreement.signatory_info);
    } else {
      setSignatoryInfo({
        registeredBusinessName: primaryCommunity?.name ?? "",
        registeredBusinessEntity: "",
        adminSignatoryName: "",
        adminSignatoryJobTitle: "",
        selectedResourceIds: [],
      });
    }}, [savedAgreement?.signatory_info, primaryCommunity?.name, savedAgreement?.security_deposit]);

  const [securityDeposit, setSecurityDeposit] = useState<number>(0);
  const [agreementDetailsExpanded, setAgreementDetailsExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  // const hasExistingAgreement = Boolean(savedAgreement?.url || savedAgreement?.last_updated);

  useEffect(() => {
    if (!actionsMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target as Node)) {
        setActionsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [actionsMenuOpen]);

  const openEditor = useCallback((html: string) => {
    setEditorHtml(html);
    setMode("editor");
    setUploadError(null);
    setUploadStatus("idle");
  }, []);

  /** Opens editor with content; injects community logo at top when available (upload, template, or blank). */
  const openEditorWithLogo = useCallback(
    (html: string) => {
      openEditor(injectLogoAtTop(html, primaryCommunity?.logo));
    },
    [openEditor, primaryCommunity?.logo]
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";
      setUploadStatus("parsing");
      setUploadError(null);
      setLastFileName(file.name);
      try {
        const html = await parseFileToHtml(file);
        setUploadStatus("success");
        openEditorWithLogo(html);
        toast.success("Document imported. You can now edit it below.");
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to parse document.";
        setUploadError(msg);
        setUploadStatus("error");
        toast.error(msg);
      }
    },
    [openEditorWithLogo]
  );

  const clearUploadState = useCallback(() => {
    setUploadStatus("idle");
    setUploadError(null);
    setLastFileName(null);
    fileInputRef.current?.click();
  }, []);

  const handleContinueDrafting = useCallback(() => {
    openEditor(BLANK_TEMPLATE_HTML);
    toast.success("Started new document.");
  }, [openEditor]);

  const handleSelectTemplate = useCallback(
    async (templateId: string) => {
      const t = TEMPLATES.find((x) => x.id === templateId);
      if (!t) return;
      if (templateId === "dol") {
        setDolLoading(true);
        try {
          const html = await fetchDOLTemplateHtml(primaryCommunity, signatoryInfo, plans ?? null, requirements ?? null, selectedResources?.length ? selectedResources : null, securityDeposit);
          openEditorWithLogo(html);
          toast.success(`Using template: ${t.name}`);
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Could not load DOL template.";
          toast.error(msg);
        } finally {
          setDolLoading(false);
        }
        return;
      }
      if ("html" in t && t.html) {
        openEditorWithLogo(t.html);
        toast.success(`Using template: ${t.name}`);
      }
    },
    [openEditorWithLogo, primaryCommunity, signatoryInfo, plans, requirements, selectedResources, securityDeposit]
  );


  // const handleLoadSavedAgreement = useCallback(
  //   async (templateId: string) => {
  //     const t = TEMPLATES.find((x) => x.id === templateId);
  //     if (!t) return;
  //     if (templateId === "dol") {
  //       setDolLoading(true);
  //       try {
  //         const html = await fetchDOLTemplateHtml(primaryCommunity, signatoryInfo);
  //         openEditorWithLogo(html);
  //         toast.success(`Using template: ${t.name}`);
  //       } catch (err) {
  //         const msg = err instanceof Error ? err.message : "Could not load DOL template.";
  //         toast.error(msg);
  //       } finally {
  //         setDolLoading(false);
  //       }
  //       return;
  //     }
  //     if ("html" in t && t.html) {
  //       openEditorWithLogo(t.html);
  //       toast.success(`Using template: ${t.name}`);
  //     }
  //   },
  //   [openEditorWithLogo, primaryCommunity, signatoryInfo]
  // );

  const handleLoadSavedAgreement = useCallback(async () => {
    const cid = communityId || primaryCommunity?.id;
    if (!cid) {
      toast.error("Community not loaded.");
      return;
    }
    try {
      if (!savedAgreement?.url) {
        toast.error("No saved agreement found for this community.");
        return;
      }
      const res = await fetch(savedAgreement.url);
      if (!res.ok) {
        throw new Error(res.status === 404 ? "Agreement file not found. It may have been deleted or the link may have expired." : "Failed to download agreement file.");
      }
      const urlLower = savedAgreement.url.toLowerCase();
      const contentType = res.headers.get("content-type") ?? "";
      const isHtml =
        urlLower.includes(".html") ||
        urlLower.includes(".htm") ||
        contentType.includes("text/html");
      let html: string;
      if (isHtml) {
        html = await res.text();
      } else {
        const arrayBuffer = await res.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        html = result.value;
      }
      const info = savedAgreement.signatory_info;
      if (info) {
        setSignatoryInfo({
          registeredBusinessName: info.registeredBusinessName ?? primaryCommunity?.name ?? "",
          registeredBusinessEntity: info.registeredBusinessEntity ?? "",
          adminSignatoryName: info.adminSignatoryName ?? "",
          adminSignatoryJobTitle: info.adminSignatoryJobTitle ?? "",
          selectedResourceIds: info.selectedResourceIds ?? [],
        });
      }
      const agreement = savedAgreement as CommunityAgreementSchema;
      if (agreement.security_deposit != null && typeof agreement.security_deposit === "number") {
        setSecurityDeposit(agreement.security_deposit);
      }
      openEditor(html);
      toast.success("Saved agreement loaded. You can edit it below.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load saved agreement.";
      toast.error(msg);
    }
  }, [communityId, primaryCommunity?.id, primaryCommunity?.name, savedAgreement, openEditor]);

  const handleBackToStart = useCallback(() => {
    setMode("dashboard");
    setSetupStep("options");
    setActiveRowMenu(null);
    setEditorHtml("");
    setUploadError(null);
    setUploadStatus("idle");
    setLastFileName(null);
  }, []);

  const handleBackToSetup = useCallback(() => {
    setMode("dashboard");
    setSetupStep("options");
    setActiveRowMenu(null);
    setEditorHtml("");
    setUploadError(null);
    setUploadStatus("idle");
    setLastFileName(null);
  }, []);

  const handleSendAgreementReminder = useCallback(
    async (row: AgreementRow) => {
      const memberEntry = consolidatedMembers.find(
        ({ member }) => memberAgreementRowId(member) === row.id
      );
      const member = memberEntry?.member;
      const memberUid = member?.uid ?? row.id;

      if (!memberUid) {
        toast.error("Could not send reminder. Member not found.");
        return;
      }

      const content = buildAgreementReminderMessage(row.memberName, row.title, primaryCommunity?.name);
      setActiveRowMenu(null);
      setSendingReminderUid(memberUid);

      try {
        await sendMessageAsync(memberUid, content);
        toast.success(`Reminder sent to ${row.memberName}.`);
      } catch {
        toast.error(`Failed to send reminder to ${row.memberName}. Please try again.`);
      } finally {
        setSendingReminderUid(null);
      }
    },
    [consolidatedMembers, sendMessageAsync, primaryCommunity?.name]
  );

  const handleViewMemberAgreement = useCallback(
    (row: AgreementRow) => {
      const memberEntry = consolidatedMembers.find(
        ({ member }) => memberAgreementRowId(member) === row.id
      );
      const fileUrl = resolveAbsoluteFileUrl(memberEntry?.member?.member_agreement?.file_url);

      if (!fileUrl) {
        toast.error("Member agreement file is unavailable or invalid.");
        setActiveRowMenu(null);
        return;
      }

      setMemberAgreementPreview({
        id: MEMBERSHIP_AGREEMENT_REQUIREMENT_ID,
        code: "MEMBER_AGREEMENT",
        name: row.title,
        is_met: true,
        is_required: true,
        presigned_url: fileUrl,
        expiration: "",
        credential_number: "",
        municipality: "",
      });
      setActiveRowMenu(null);
    },
    [consolidatedMembers]
  );

  const handleCloseMemberAgreementPreview = useCallback(() => {
    setMemberAgreementPreview(null);
  }, []);

  const handleViewMemberDetails = useCallback(
    (row: AgreementRow) => {
      const memberEntry = consolidatedMembers.find(
        ({ member }) => memberAgreementRowId(member) === row.id
      );
      const member = memberEntry?.member;

      if (!member) {
        toast.error("Could not open member profile. Member not found.");
        return;
      }

      const resolvedCommunityId = member.community_id || communityId || primaryCommunity?.id || "";
      setSelectedMember(
        resolvedCommunityId && !member.community_id
          ? { ...member, community_id: resolvedCommunityId }
          : member
      );
      setViewMemberDetails(true);
      setActiveRowMenu(null);
    },
    [consolidatedMembers, communityId, primaryCommunity?.id]
  );

  const handleCloseMemberDetails = useCallback(() => {
    setViewMemberDetails(false);
  }, []);

  const openNewAgreement = useCallback((step: SetupStep = "options") => {
    setSetupStep(step);
    setMode("setup");
  }, []);

  /** Save current editor HTML as agreement document via API (addMemberAgreement). */
  const handleSaveDraft = useCallback(
    async (html: string) => {
      const cid = communityId || primaryCommunity?.id;
      if (!cid) {
        toast.error("Community not loaded.");
        return;
      }
      setSaveDraftLoading(true);
      try {
        const agreementPayload = {
          name: primaryCommunity?.name ? `${primaryCommunity.name} Member Agreement` : "Member Agreement",
          description: "Member agreement draft",
          content: html,
          signatory_info: {
            registeredBusinessName: signatoryInfo.registeredBusinessName ?? "",
            registeredBusinessEntity: signatoryInfo.registeredBusinessEntity ?? "",
            adminSignatoryName: signatoryInfo.adminSignatoryName ?? "",
            adminSignatoryJobTitle: signatoryInfo.adminSignatoryJobTitle ?? "",
            selectedResourceIds: signatoryInfo.selectedResourceIds ?? [],
          },
          security_deposit: securityDeposit,
        } as CommunityAgreementSchema;
        const ok = await addMemberAgreement(agreementPayload);
        if (ok) {
          toast.success("Agreement saved as document.");
          reloadAgreement?.();
          handleBackToStart();
        } else {
          toast.error("Failed to save agreement.");
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to save agreement.";
        toast.error(msg);
      } finally {
        setSaveDraftLoading(false);
      }
    },
    [communityId, primaryCommunity?.id, primaryCommunity?.name, signatoryInfo, securityDeposit, addMemberAgreement, reloadAgreement, handleBackToStart]
  );

  const toggleResourceSelection = useCallback((resourceId: string) => {
    setSignatoryInfo((prev) =>
      prev.selectedResourceIds?.includes(resourceId) ? { ...prev, selectedResourceIds: prev.selectedResourceIds?.filter((id) => id !== resourceId) ?? [] } : { ...prev, selectedResourceIds: [...(prev.selectedResourceIds ?? []), resourceId] }
    );
  }, []);

  const updateSignatory = useCallback((field: keyof AgreementSignatoryInfo, value: string) => {
    setSignatoryInfo((prev) => ({ ...prev, [field]: value }));
  }, []);

  if (isErrorResources && error?.message) {
    toast.error(error?.message);
  }

  const agreementSignatoryFields = (
    <AgreementSignatorySettingsFields
      signatoryInfo={signatoryInfo}
      securityDeposit={securityDeposit}
      resourcesOffered={resourcesOffered}
      onSignatoryChange={updateSignatory}
      onSecurityDepositChange={setSecurityDeposit}
      onToggleResourceSelection={toggleResourceSelection}
    />
  );

  const agreementDetailsSection = (
    <article className="mt-6 rounded-2xl border border-primary-300/40 bg-[#EFF8FA]/40 p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-raleway font-semibold uppercase tracking-[0.16em] text-primary-300">
            Agreement settings
          </p>
          <h2 className="mt-2 font-raleway text-[24px] font-bold text-[#333333]">
            Configure signatory and business details
          </h2>
          <p className="mt-2 max-w-2xl text-[15px] font-open-sans text-[#666666]">
            Set the registered business, community signatory, security deposit, and resources covered by this agreement.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAgreementDetailsExpanded(false)}
          className="text-[13px] font-open-sans font-semibold text-[#808080] transition-colors hover:text-primary-200"
        >
          Close
        </button>
      </div>
      {agreementSignatoryFields}
    </article>
  );

  const hasSavedAgreementWithDate = Boolean(savedAgreement && savedAgreement.last_updated);

  const communityDraftRow = useMemo((): AgreementRow | null => {
    if (!hasSavedAgreementWithDate || !savedAgreement?.last_updated) return null;
    const adminSignatory: AdminSignatoryDetails = {
      registeredBusinessName: signatoryInfo.registeredBusinessName ?? "",
      registeredBusinessEntity: signatoryInfo.registeredBusinessEntity ?? "",
      adminSignatoryName: signatoryInfo.adminSignatoryName ?? "",
      adminSignatoryJobTitle: signatoryInfo.adminSignatoryJobTitle ?? "",
    };
    const signatoryName =
      adminSignatory.adminSignatoryName.trim() || primaryCommunity?.name?.trim() || "Community admin";
    return {
      id: "Admin",
      title: communityAgreementTitle,
      memberName: signatoryName,
      tier: formatAdminBusinessDisplay(adminSignatory),
      status: "draft",
      lastUpdated: moment.utc(savedAgreement.last_updated).tz(communityTimezone).fromNow(),
      signatures: { required: 1, completed: 0 },
      updatedAtMs: moment.utc(savedAgreement.last_updated).valueOf(),
      isCommunityDraft: true,
      adminSignatory,
    };
  }, [
    hasSavedAgreementWithDate,
    savedAgreement?.last_updated,
    communityAgreementTitle,
    signatoryInfo.registeredBusinessName,
    signatoryInfo.registeredBusinessEntity,
    signatoryInfo.adminSignatoryName,
    signatoryInfo.adminSignatoryJobTitle,
    primaryCommunity?.name,
    communityTimezone,
  ]);

  const memberAgreementRows = useMemo(
    () => buildAgreementRowsFromMembers(consolidatedMembers, communityAgreementTitle, communityTimezone),
    [consolidatedMembers, communityAgreementTitle, communityTimezone]
  );

  const agreementRows = useMemo(() => {
    if (communityDraftRow) return [communityDraftRow, ...memberAgreementRows];
    return memberAgreementRows;
  }, [communityDraftRow, memberAgreementRows]);

  const signatureHistory = useMemo(
    () => buildSignatureHistory(consolidatedMembers, communityAgreementTitle, communityTimezone),
    [consolidatedMembers, communityAgreementTitle, communityTimezone]
  );

  const filteredAgreements = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return agreementRows;
    return agreementRows.filter((row) => {
      const admin = row.adminSignatory;
      return (
        row.title.toLowerCase().includes(q) ||
        row.memberName.toLowerCase().includes(q) ||
        row.id.toLowerCase().includes(q) ||
        row.tier.toLowerCase().includes(q) ||
        (admin &&
          (admin.adminSignatoryName.toLowerCase().includes(q) ||
            admin.adminSignatoryJobTitle.toLowerCase().includes(q) ||
            admin.registeredBusinessName.toLowerCase().includes(q) ||
            admin.registeredBusinessEntity.toLowerCase().includes(q)))
      );
    });
  }, [agreementRows, searchQuery]);

  const sortedFilteredAgreements = useMemo(() => {
    const communityDraftRows = filteredAgreements.filter((row) => row.isCommunityDraft);
    const memberRows = filteredAgreements.filter((row) => !row.isCommunityDraft);
    const sortedMemberRows = sortAgreementRows(memberRows, agreementSortField, agreementSortDirection);
    return [...communityDraftRows, ...sortedMemberRows];
  }, [filteredAgreements, agreementSortField, agreementSortDirection]);

  const handleAgreementSort = useCallback((field: AgreementSortField) => {
    if (field === agreementSortField) {
      setAgreementSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setAgreementSortField(field);
    setAgreementSortDirection(getDefaultAgreementSortDirection(field));
  }, [agreementSortField]);

  const filteredMemberAgreements = useMemo(
    () => filteredAgreements.filter((row) => !row.isCommunityDraft),
    [filteredAgreements]
  );

  const filteredCommunityDrafts = useMemo(
    () => filteredAgreements.filter((row) => row.isCommunityDraft),
    [filteredAgreements]
  );

  const agreementTrackingSubtitle = useMemo(() => {
    if (filteredMemberAgreements.length === 0 && filteredCommunityDrafts.length === 0) {
      return "No members to show yet";
    }
    if (filteredMemberAgreements.length === 0) {
      return "Community agreement draft saved - member activity will appear here";
    }
    const draftSuffix =
      filteredCommunityDrafts.length > 0 ? " · includes community agreement draft" : "";
    return `Showing 1 - ${filteredMemberAgreements.length} of ${filteredMemberAgreements.length} member${filteredMemberAgreements.length === 1 ? "" : "s"}${draftSuffix}`;
  }, [filteredMemberAgreements.length, filteredCommunityDrafts.length]);

  const handleUploadAgreementClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const statCards = useMemo(
    () => {
      const values = [
        String(memberAgreementRows.length),
        String(memberAgreementRows.filter((row) => row.status === "sent" || row.status === "viewed").length),
        String(memberAgreementRows.filter((row) => row.status === "signed").length),
        String(memberAgreementRows.filter((row) => row.status === "expired").length),
      ];

      return AGREEMENT_STATS.map((stat, index) => ({
        label: stat.label,
        detail: stat.detail,
        value: values[index],
      }));
    },
    [memberAgreementRows]
  );

  const hiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept={ACCEPTED_TYPES}
      onChange={handleFileSelect}
      className="hidden"
    />
  );

  const uploadDropzone = (
    <>
      {uploadStatus === "parsing" && (
        <div className="flex flex-col items-center py-8">
          <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-transparent animate-spin mb-3" />
          <p className="text-primary-200 font-raleway font-medium">Importing document…</p>
          {lastFileName && (
            <p className="text-[13px] text-[#666] font-open-sans mt-1 truncate max-w-full px-4">{lastFileName}</p>
          )}
        </div>
      )}
      {uploadStatus === "success" && (
        <div className="flex flex-col items-center py-8">
          <CheckCircleOutlineIcon sx={{ fontSize: 52 }} className="text-emerald-600 mb-2" />
          <p className="text-emerald-800 font-raleway font-semibold">Document imported</p>
          {lastFileName && (
            <p className="text-emerald-700 text-[14px] font-open-sans mt-1 truncate max-w-full px-4">{lastFileName}</p>
          )}
        </div>
      )}
      {uploadStatus === "error" && (
        <div className="flex flex-col items-center py-8">
          <ErrorOutlineIcon sx={{ fontSize: 52 }} className="text-red-500 mb-2" />
          <p className="text-red-800 font-raleway font-semibold">Upload failed</p>
          {uploadError && <p className="text-red-600 text-[13px] mt-2 font-open-sans max-w-md text-center">{uploadError}</p>}
          <button
            type="button"
            onClick={clearUploadState}
            className="mt-4 px-4 py-2 rounded-lg bg-red-100 text-red-700 font-raleway font-medium text-sm hover:bg-red-200 transition-colors"
          >
            Try another file
          </button>
        </div>
      )}
    </>
  );

  return (
    <>
      <MemberAgreementPreviewModal
        isOpen={Boolean(memberAgreementPreview)}
        credential={memberAgreementPreview}
        closeAppModal={handleCloseMemberAgreementPreview}
      />
      {selectedMember && (
        <MemberDetailsModal
          isOpen={viewMemberDetails}
          closeAppModal={handleCloseMemberDetails}
          member={selectedMember}
        />
      )}
    <div className="w-full px-6 my-6 min-h-[620px]">
      {mode === "dashboard" && (
        <section className="flex flex-col gap-6">
          <AgreementDashboardHeader
            hasSavedAgreement={hasSavedAgreementWithDate}
            agreementTitle={communityAgreementTitle}
            lastUpdatedLabel={
              hasSavedAgreementWithDate && savedAgreement?.last_updated
                ? moment.utc(savedAgreement.last_updated).tz(communityTimezone).format("MMM D, YYYY, h:mm A")
                : undefined
            }
            uploadStatus={uploadStatus}
            actionsMenuOpen={actionsMenuOpen}
            setActionsMenuOpen={setActionsMenuOpen}
            actionsRef={actionsRef}
            onUpload={handleUploadAgreementClick}
            onStartFromScratch={() => openNewAgreement("options")}
            onUseTemplate={() => openNewAgreement("templates")}
            onViewAgreement={handleLoadSavedAgreement}
            viewLoading={savedAgreementLoading}
          />

          {agreementDetailsExpanded ? (
            agreementDetailsSection
          ) : (<></>)}

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {statCards.map((stat) => (
              <article key={stat.label} className="rounded-2xl border border-[#E9E9E9] bg-white p-5 shadow-sm">
                <p className="text-[13px] font-open-sans text-[#808080]">{stat.label}</p>
                <p className="mt-2 font-raleway text-[32px] font-bold text-primary-200">{stat.value}</p>
                <p className="mt-1 text-[12px] font-open-sans text-[#999999]">{stat.detail}</p>
              </article>
            ))}
          </div>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
            <article className="overflow-visible rounded-2xl border border-[#E9E9E9] bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#F0F0F0] px-6 py-5">
                <div>
                  <h2 className="font-raleway text-[18px] font-semibold text-primary-200">Agreement tracking</h2>
                  <p className="mt-1 text-[14px] font-open-sans text-[#4D4D4D]">
                    {agreementTrackingSubtitle}
                  </p>
                </div>

                <label className="relative min-w-[240px] flex-1 sm:w-auto sm:flex-none">
                    <MaskedIcon
                      src="/images/memberships/tabler_search.svg"
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 bg-[#808080]"
                    />
                    <input
                      type="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search for member name..."
                      className="h-11 w-full rounded-lg border border-[#E9E9E9] bg-white pl-10 pr-3 text-[14px] font-open-sans text-[#333333] placeholder:text-[#808080] focus:border-primary-200 focus:outline-none focus:ring-1 focus:ring-primary-200"
                    />
                  </label>
              </div>

              {filteredAgreements.length === 0 ? (
                <AgreementTrackingEmptyState onUpload={handleUploadAgreementClick} />
              ) : (
                <div className="overflow-x-auto p-1">
                  <table className="min-w-[860px] w-full text-left">
                    <thead>
                      <tr className="border-b border-[#F0F0F0] bg-[#FAFAFA]">
                        <SortableHeader
                          label="Member"
                          field="member"
                          sortField={agreementSortField}
                          sortDirection={agreementSortDirection}
                          onSort={handleAgreementSort}
                          cellClassName={AGREEMENT_HEADER_CELL}
                        />
                        <SortableHeader
                          label="Tier"
                          field="tier"
                          sortField={agreementSortField}
                          sortDirection={agreementSortDirection}
                          onSort={handleAgreementSort}
                          cellClassName={AGREEMENT_HEADER_CELL}
                        />
                        <SortableHeader
                          label="Status"
                          field="status"
                          sortField={agreementSortField}
                          sortDirection={agreementSortDirection}
                          onSort={handleAgreementSort}
                          cellClassName={AGREEMENT_HEADER_CELL}
                        />
                        <SortableHeader
                          label="Signatures"
                          field="signatures"
                          sortField={agreementSortField}
                          sortDirection={agreementSortDirection}
                          onSort={handleAgreementSort}
                          cellClassName={AGREEMENT_HEADER_CELL}
                        />
                        <SortableHeader
                          label="Updated"
                          field="updated"
                          sortField={agreementSortField}
                          sortDirection={agreementSortDirection}
                          onSort={handleAgreementSort}
                          cellClassName={AGREEMENT_HEADER_CELL}
                        />
                        <th scope="col" className={`${AGREEMENT_HEADER_CELL} text-right`}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFilteredAgreements.map((row) =>
                        row.isCommunityDraft && row.adminSignatory ? (
                          <AgreementTrackingAdminRow
                            key={row.id}
                            row={{ ...row, adminSignatory: row.adminSignatory }}
                            activeRowMenu={activeRowMenu}
                            savedAgreementLoading={savedAgreementLoading}
                            onToggleMenu={(rowId) =>
                              setActiveRowMenu((current) => (current === rowId ? null : rowId))
                            }
                            onViewAgreement={handleLoadSavedAgreement}
                            onEditSettings={() => setAgreementDetailsExpanded(true)}
                            onCloseMenu={() => setActiveRowMenu(null)}
                          />
                        ) : (
                          <AgreementTrackingMemberRow
                            key={row.id}
                            row={row}
                            activeRowMenu={activeRowMenu}
                            onToggleMenu={(rowId) =>
                              setActiveRowMenu((current) => (current === rowId ? null : rowId))
                            }
                            onCloseMenu={() => setActiveRowMenu(null)}
                            onSendReminder={handleSendAgreementReminder}
                            onViewAgreement={handleViewMemberAgreement}
                            onViewMemberDetails={handleViewMemberDetails}
                            isSendingReminder={sendingReminderUid === row.id}
                          />
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </article>

            <div className="flex flex-col gap-6">
              {/* <article className="rounded-2xl border border-[#E9E9E9] bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h2 className="font-raleway text-[18px] font-bold text-[#333333]">Templates</h2>
                    <p className="mt-1 text-[13px] font-open-sans text-[#808080]">Reuse common agreement structures.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => openNewAgreement("templates")}
                    aria-label="Browse templates"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-primary-300 transition-colors hover:bg-[#EFF8FA] hover:text-primary-200"
                  >
                    <PostAddIcon sx={{ fontSize: 22 }} />
                  </button>
                </div>
                <div className="flex flex-col gap-3">
                  {SIDEBAR_TEMPLATES.map((template) => (
                    <button
                      key={template.name}
                      type="button"
                      onClick={() => openNewAgreement("templates")}
                      className="rounded-xl border border-[#F0F0F0] bg-[#FAFAFA] p-4 text-left transition-colors hover:border-primary-200 hover:bg-[#EFF8FA]"
                    >
                      <p className="font-raleway text-[14px] font-semibold text-[#333333]">{template.name}</p>
                      <p className="mt-1 text-[12px] font-open-sans text-[#808080]">{template.description}</p>
                    </button>
                  ))}
                </div>
              </article> */}

              <article className="rounded-2xl border border-[#E9E9E9] bg-white p-6 shadow-sm">
                <div className="mb-4 flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EFF8FA] text-primary-300">
                    <HistoryIcon sx={{ fontSize: 22 }} />
                  </span>
                  <div>
                    <h2 className="font-raleway text-[18px] font-bold text-[#333333]">Signature history</h2>
                    <p className="mt-1 text-[13px] font-open-sans text-[#808080]">Latest contract activity.</p>
                  </div>
                </div>
                <ul className="flex flex-col gap-4">
                  {signatureHistory.map((item) => (
                    <li key={item.id} className="flex gap-3">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-300" />
                      <div>
                        <p className="text-[13px] font-open-sans text-[#333333]">{item.event}</p>
                        <p className="mt-1 text-[12px] font-open-sans text-[#999999]">{item.meta}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </article>
            </div>
          </div>
        </section>
      )}

      {mode === "setup" && (
        <section className="min-h-[620px]">
          <div className="flex items-start justify-between gap-4">
            <div className="text-left">
              <button
                type="button"
                onClick={handleBackToStart}
                className="flex items-center gap-2 mb-2 text-[13px] font-raleway text-primary-200 hover:bg-primary-200/10 rounded-lg p-2 transition-colors"
              >
                <ArrowBackIcon className="my-auto h-4 w-4" sx={{ fontSize: 20 }} />
                <span className="my-auto text-[14px] font-raleway font-semibold text-primary-200">Back</span>
              </button>
              {/* {setupStep === "options" && (
                <div className="max-w-2xl">
                  <h1 className="font-raleway text-[20px] font-semibold text-[#333333]">
                    Choose how you would like to set up the contract for your packages.
                  </h1>
                  <p className="mt-2 text-[15px] font-open-sans text-[#4D4D4D]">
                    Use a template from the library or ones you have saved.
                  </p>
                </div>
              )} */}
              {setupStep === "templates" && (
                <>
                  <h1 className="font-raleway text-[24px] font-bold text-[#333333]">Choose a template</h1>
                  <p className="mt-2 max-w-md text-[15px] font-open-sans text-[#4D4D4D]">
                    Select a starting point for your member agreement.
                  </p>
                </>
              )}
            </div>
            {setupStep !== "options" && !agreementDetailsExpanded && (
                <button
                  type="button"
                  onClick={() => setAgreementDetailsExpanded(true)}
                  className="text-[13px] font-raleway text-primary-300 hover:underline"
                >
                  Edit agreement settings
                </button>
              )}
          </div>

          {agreementDetailsExpanded && agreementDetailsSection}

          {setupStep === "options" ? (
            <article className="mt-2 rounded-2xl border border-primary-300/40 bg-[#EFF8FA]/40 p-6 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[12px] font-raleway font-semibold uppercase tracking-[0.16em] text-primary-300">
                    Create from scratch
                  </p>
                  <h1 className="mt-2 font-raleway text-[24px] font-bold text-[#333333]">
                    Draft a custom membership agreement
                  </h1>
                  <p className="mt-2 max-w-2xl text-[15px] font-open-sans text-[#666666]">
                    Set the registered business, community signatory, security deposit, and resources covered by this agreement before you start drafting.
                  </p>
                </div>
              </div>
              {agreementSignatoryFields}
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleContinueDrafting}
                  className="inline-flex h-11 items-center gap-2 rounded-lg bg-primary-200 px-5 font-raleway text-[14px] font-semibold text-white transition-colors hover:bg-[#002a35]"
                >
                  <EditIcon sx={{ fontSize: 18 }} />
                  Continue drafting
                </button>
              </div>
            </article>
          ) : (
            <>
              <div className="mt-6 grid gap-6 md:grid-cols-3">
                {TEMPLATES.map((template) => {
                  const icons = TEMPLATE_SETUP_ICONS[template.id] ?? TEMPLATE_SETUP_ICONS.blank;
                  const isLoading = template.id === "dol" && dolLoading;
                  return (
                    <AgreementSetupOption
                      key={template.id}
                      iconSrc={icons.iconSrc}
                      activeIconSrc={icons.activeIconSrc}
                      title={template.name}
                      description={template.description}
                      onClick={() => handleSelectTemplate(template.id)}
                      disabled={isLoading}
                      meta={isLoading ? "Loading template…" : undefined}
                      actionLabel={isLoading ? "Loading…" : "Get started"}
                      actionAriaLabel={template.name}
                    />
                  );
                })}
                {hasSavedAgreementWithDate && (
                  <AgreementSetupOption
                    iconSrc={TEMPLATE_SETUP_ICONS.saved.iconSrc}
                    activeIconSrc={TEMPLATE_SETUP_ICONS.saved.activeIconSrc}
                    title="Load saved agreement"
                    description="Open the agreement previously saved for this community."
                    onClick={handleLoadSavedAgreement}
                    disabled={savedAgreementLoading}
                    meta={`Last updated: ${moment.utc(savedAgreement!.last_updated!).tz(primaryCommunity?.timezone || "America/New_York").format("MMM D, YYYY, h:mm A")}`}
                    actionLabel={savedAgreementLoading ? "Loading…" : "Open agreement"}
                    actionAriaLabel="Load saved agreement"
                  />
                )}
              </div>
              {/* <button
                type="button"
                onClick={() => setSetupStep("options")}
                className="mt-6 text-[14px] font-raleway text-primary-200 hover:underline"
              >
                ← Back to setup options
              </button> */}
            </>
          )}

          {uploadDropzone}
        </section>
      )}

      {mode === "editor" && (
        <div className="flex flex-col gap-4">
          {agreementDetailsExpanded ? (
            agreementDetailsSection
          ) : (
            <div className="flex justify-end">
              {/* <button
                type="button"
                onClick={() => setAgreementDetailsExpanded(true)}
                className="text-[13px] font-raleway text-primary-200 hover:underline"
              >
                Edit agreement settings
              </button> */}
            </div>
          )}
          <EditorMode
            handleBackToStart={handleBackToSetup}
            editorHtml={editorHtml}
            setEditorHtml={setEditorHtml}
            onSaveDraft={handleSaveDraft}
            saveLoading={saveDraftLoading}
            contractTitle={communityAgreementTitle}
          />
        </div>
      )}
      {hiddenFileInput}
    </div>
    </>
  );
}


type EditorModeProps = {
  handleBackToStart: () => void;
  editorHtml: string;
  setEditorHtml: (html: string) => void;
  onSaveDraft: (html: string) => Promise<void>;
  saveLoading: boolean;
  contractTitle: string;
};

const EditorMode: FC<EditorModeProps> = ({
  handleBackToStart,
  editorHtml,
  setEditorHtml,
  onSaveDraft,
  saveLoading,
  contractTitle,
}) => {
  const handleSave = () => {
    onSaveDraft(editorHtml);
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-[#E9E9E9] bg-white shadow-sm">
      <header className="flex h-14 flex-wrap items-center justify-between gap-3 border-b border-[#E9E9E9]/70 bg-white px-4">
        <div className="flex min-w-0 items-center gap-4">
          <button
            type="button"
            onClick={handleBackToStart}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[#737373] transition-colors hover:bg-[#f8f8f8] hover:text-primary-200"
            aria-label="Back to setup options"
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </button>
          <div className="hidden h-6 w-px bg-[#E9E9E9] sm:block" />
          <div className="flex min-w-0 items-center gap-2">
            <p className="truncate font-raleway text-[16px] font-bold text-primary-200 sm:max-w-[320px]">
              {contractTitle}
            </p>
            <span className="shrink-0 rounded bg-[#f8f8f8] px-3 py-1 font-raleway text-[11px] font-semibold uppercase tracking-[0.12em] text-[#737373]">
              Draft
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSave}
          disabled={saveLoading}
          className="h-10 rounded-lg bg-primary-200 px-5 font-raleway text-[14px] font-medium text-white transition-colors hover:bg-[#002a35] disabled:cursor-wait disabled:opacity-60"
        >
          {saveLoading ? "Saving…" : "Save document"}
        </button>
      </header>

      <AgreementEditor
        key={editorHtml?.slice(0, 100)}
        initialHtml={editorHtml}
        onContentChange={setEditorHtml}
        contractTitle={contractTitle}
      />
    </div>
  );
};

const AgreementTrackingAdminRow = ({
  row,
  activeRowMenu,
  savedAgreementLoading,
  onToggleMenu,
  onViewAgreement,
  onEditSettings,
  onCloseMenu,
}: {
  row: AgreementRow & { adminSignatory: AdminSignatoryDetails };
  activeRowMenu: string | null;
  savedAgreementLoading: boolean;
  onToggleMenu: (rowId: string) => void;
  onViewAgreement: () => void;
  onEditSettings: () => void;
  onCloseMenu: () => void;
}) => {
  const { adminSignatory } = row;
  const signatoryName = adminSignatory.adminSignatoryName.trim() || row.memberName;
  const jobTitle = adminSignatory.adminSignatoryJobTitle.trim();
  const businessName = adminSignatory.registeredBusinessName.trim();
  const businessEntity = adminSignatory.registeredBusinessEntity.trim();

  return (
    <tr className="border-b border-[#D9EEF4] bg-[#EFF8FA]/60 last:border-0">
      {/* <td className="border-l-4 border-l-primary-200 px-5 py-4">
        <button
          type="button"
          onClick={onViewAgreement}
          className="text-left"
          disabled={savedAgreementLoading}
        >
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-raleway text-[14px] font-semibold text-primary-200 hover:text-[#002a35]">
              {row.title}
            </p>
            <span className="inline-flex rounded-full bg-primary-200/10 px-2 py-0.5 text-[10px] font-raleway font-semibold uppercase tracking-wide text-primary-200">
              Community admin
            </span>
          </div>
          <p className="mt-1 text-[12px] font-open-sans text-[#5DB6CE]">Community agreement draft</p>
        </button>
      </td> */}
      <td className="px-5 py-4">
        <p className="text-[11px] font-raleway font-semibold uppercase tracking-wide text-[#5DB6CE]">
          Signatory
        </p>
        <p className="mt-1 text-[14px] font-open-sans font-semibold text-[#333333]">{signatoryName}</p>
        {jobTitle ? (
          <p className="text-[12px] font-open-sans text-[#666666]">{jobTitle}</p>
        ) : (
          <p className="text-[12px] font-open-sans italic text-[#999999]">No job title set</p>
        )}
      </td>
      <td className="px-5 py-4">
        <p className="text-[11px] font-raleway font-semibold uppercase tracking-wide text-[#5DB6CE]">
          Business
        </p>
        {businessName ? (
          <p className="mt-1 text-[14px] font-open-sans font-semibold text-[#333333]">{businessName}</p>
        ) : (
          <p className="mt-1 text-[14px] font-open-sans italic text-[#999999]">No business name set</p>
        )}
        {businessEntity ? (
          <p className="text-[12px] font-open-sans text-[#666666]">{businessEntity}</p>
        ) : null}
      </td>
      <td className="px-5 py-4">
        <span className="inline-flex rounded-xl bg-[#D9EEF4] px-2.5 py-1 text-[12px] font-raleway font-semibold text-primary-200">
          Saved
        </span>
      </td>
      <td className="px-5 py-4">
        <span className="text-[13px] font-open-sans text-[#666666]">Admin</span>
      </td>
      <td className="px-5 py-4 text-[13px] font-open-sans text-[#808080]">{row.lastUpdated}</td>
      <td className="relative px-5 py-4 text-right">
        <button
          type="button"
          aria-label={`Open actions for ${row.title}`}
          onClick={() => onToggleMenu(row.id)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#C5E4ED] bg-white text-primary-200 transition-colors hover:border-primary-200 hover:bg-[#EFF8FA]"
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </button>
        {activeRowMenu === row.id && (
          <div className="absolute right-5 top-12 z-20 w-48 rounded-xl border border-[#C5E4ED] bg-white p-1.5 text-left shadow-lg">
            <button
              type="button"
              onClick={() => {
                onViewAgreement();
                onCloseMenu();
              }}
              className="flex w-full items-center rounded-lg px-3 py-2 text-left text-[13px] font-open-sans text-[#666666] transition-colors hover:bg-[#EFF8FA] hover:text-primary-200"
            >
              View agreement
            </button>
            <button
              type="button"
              onClick={() => {
                onEditSettings();
                onCloseMenu();
              }}
              className="flex w-full items-center rounded-lg px-3 py-2 text-left text-[13px] font-open-sans text-[#666666] transition-colors hover:bg-[#EFF8FA] hover:text-primary-200"
            >
              Edit signatory settings
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

const AgreementTrackingMemberRow = ({
  row,
  activeRowMenu,
  onToggleMenu,
  onCloseMenu,
  onSendReminder,
  onViewAgreement,
  onViewMemberDetails,
  isSendingReminder,
}: {
  row: AgreementRow;
  activeRowMenu: string | null;
  onToggleMenu: (rowId: string) => void;
  onCloseMenu: () => void;
  onSendReminder: (row: AgreementRow) => void;
  onViewAgreement: (row: AgreementRow) => void;
  onViewMemberDetails: (row: AgreementRow) => void;
  isSendingReminder: boolean;
}) => {
  const clickableCellClass =
    "px-5 py-4 cursor-pointer transition-colors hover:bg-[#FAFAFA]";

  const handleViewMemberDetails = () => {
    onViewMemberDetails(row);
  };

  return (
    <tr className="border-b border-[#F5F5F5] last:border-0">
      <td
        className={`${clickableCellClass} text-[14px] font-open-sans text-[#333333]`}
        onClick={handleViewMemberDetails}
      >
        {row.memberName}
      </td>
      <td
        className={`${clickableCellClass} text-[14px] font-open-sans text-[#666666]`}
        onClick={handleViewMemberDetails}
      >
        {row.tier}
      </td>
      <td className={clickableCellClass} onClick={handleViewMemberDetails}>
        <span
          className={`inline-flex rounded-full px-2.5 py-1 text-[12px] font-raleway font-semibold capitalize ${STATUS_STYLES[row.status]}`}
        >
          {row.status}
        </span>
      </td>
      <td className={clickableCellClass} onClick={handleViewMemberDetails}>
        <span className="inline-flex items-center gap-1.5 text-[13px] font-open-sans text-[#666666]">
          {row.signatures.completed === row.signatures.required ? (
            <CheckCircleIcon sx={{ fontSize: 16 }} className="text-emerald-600" />
          ) : (
            <AccessTimeIcon sx={{ fontSize: 16 }} className="text-orange-500" />
          )}
          {row.signatures.completed}/{row.signatures.required}
        </span>
      </td>
      <td
        className={`${clickableCellClass} text-[13px] font-open-sans text-[#808080]`}
        onClick={handleViewMemberDetails}
      >
        {row.lastUpdated}
      </td>
      <td className="relative px-5 py-4 text-right">
        <button
          type="button"
          aria-label={`Open actions for ${row.title}`}
          onClick={(event) => {
            event.stopPropagation();
            onToggleMenu(row.id);
          }}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#E9E9E9] bg-white text-[#999999] transition-colors hover:border-primary-200 hover:text-primary-200"
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </button>
        {activeRowMenu === row.id && (
          <div className="absolute right-5 top-12 z-20 w-44 rounded-xl border border-[#E9E9E9] bg-white p-1.5 text-left shadow-lg">
            <button
              type="button"
              onClick={() => {
                onViewMemberDetails(row);
                onCloseMenu();
              }}
              className="flex w-full items-center rounded-lg px-3 py-2 text-left text-[13px] font-open-sans text-[#666666] transition-colors hover:bg-[#F5F5F5] hover:text-[#333333]"
            >
              View member
            </button>
            {row.status === "signed" && (
              <button
                type="button"
                onClick={() => {
                  onViewAgreement(row);
                  onCloseMenu();
                }}
                className="flex w-full items-center rounded-lg px-3 py-2 text-left text-[13px] font-open-sans text-[#666666] transition-colors hover:bg-[#F5F5F5] hover:text-[#333333]"
              >
                View agreement
              </button>
            )}
            {row.status !== "signed" && (
              <button
                type="button"
                onClick={() => {
                  onSendReminder(row);
                  onCloseMenu();
                }}
                disabled={isSendingReminder}
                className="flex w-full items-center rounded-lg px-3 py-2 text-left text-[13px] font-open-sans text-[#666666] transition-colors hover:bg-[#F5F5F5] hover:text-[#333333] disabled:cursor-wait disabled:opacity-60"
              >
                {isSendingReminder ? "Sending reminder..." : "Send reminder"}
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};

const MaskedIcon = ({ src, className }: { src: string; className?: string }) => {
  return (
    <span
      className={className}
      style={{
        WebkitMask: `url("${src}") center / contain no-repeat`,
        mask: `url("${src}") center / contain no-repeat`,
      }}
      aria-hidden
    />
  );
};

const AgreementHeaderMenuOption = ({
  label,
  iconSrc,
  onClick,
}: {
  label: string;
  iconSrc: string;
  onClick: () => void;
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[14px] font-open-sans text-[#111111] transition-colors hover:bg-[#F5F5F5]"
    >
      <img src={iconSrc} alt="" className="h-6 w-6 shrink-0" aria-hidden />
      {label}
    </button>
  );
};

const AgreementDashboardHeader = ({
  hasSavedAgreement,
  agreementTitle,
  lastUpdatedLabel,
  uploadStatus,
  actionsMenuOpen,
  setActionsMenuOpen,
  actionsRef,
  onUpload,
  onStartFromScratch,
  onUseTemplate,
  onViewAgreement,
  viewLoading = false,
}: {
  hasSavedAgreement: boolean;
  agreementTitle: string;
  lastUpdatedLabel?: string;
  uploadStatus: UploadStatus;
  actionsMenuOpen: boolean;
  setActionsMenuOpen: Dispatch<SetStateAction<boolean>>;
  actionsRef: RefObject<HTMLDivElement>;
  onUpload: () => void;
  onStartFromScratch: () => void;
  onUseTemplate: () => void;
  onViewAgreement: () => void;
  viewLoading?: boolean;
}) => {
  return (
    <article className="overflow-hidden rounded-2xl border border-[#E9E9E9] bg-white shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-5 p-6 pb-5">
        <div className="min-w-0 flex-1">
          <h2 className="font-raleway text-[24px] font-semibold text-primary-200">
            Create, send, and track member agreements.
          </h2>
          <p className="mt-2 max-w-2xl text-[14px] font-open-sans text-[#4D4D4D]">
            Build agreements from scratch or templates, track signature status, and review complete
            signing history for each member.
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={onUpload}
            disabled={uploadStatus === "parsing"}
            className="inline-flex h-12 items-center gap-2 rounded-lg bg-primary-200 px-6 font-raleway text-[14px] font-medium text-white transition-colors hover:bg-[#002a35] disabled:cursor-wait disabled:opacity-60"
          >
            <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />
            {uploadStatus === "parsing" ? "Uploading..." : "Upload New Agreement"}
          </button>

          <div ref={actionsRef} className="relative">
            <button
              type="button"
              onClick={() => setActionsMenuOpen((open) => !open)}
              aria-label="Agreement options"
              className={`group flex h-12 w-12 items-center justify-center rounded-full transition-colors ${
                actionsMenuOpen ? "bg-[#5DB6CE]" : "bg-[#F8F8F8] hover:bg-[#5DB6CE]"
              }`}
            >
              <img
                src="/images/memberships/iconamoon_menu-kebab-horizontal.png"
                alt=""
                aria-hidden
                className={`h-6 w-6 transition-[filter] ${
                  actionsMenuOpen
                    ? "brightness-0 invert"
                    : "group-hover:brightness-0 group-hover:invert"
                }`}
              />
            </button>

            {actionsMenuOpen && (
              <div className="absolute right-0 top-14 z-20 w-56 rounded-xl border border-[#E9E9E9] bg-white p-1.5 shadow-lg">
                <AgreementHeaderMenuOption
                  label="Start from scratch"
                  iconSrc="/images/memberships/document-add-outline.svg"
                  onClick={() => {
                    setActionsMenuOpen(false);
                    onStartFromScratch();
                  }}
                />
                <AgreementHeaderMenuOption
                  label="Use template"
                  iconSrc="/images/memberships/library-outline.svg"
                  onClick={() => {
                    setActionsMenuOpen(false);
                    onUseTemplate();
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-[#F0F0F0] px-6 py-5">
        {!hasSavedAgreement ? (
          <div
            role="status"
            aria-label="No agreement saved yet"
            className="relative overflow-hidden rounded-xl border border-dashed border-[#5DB6CE]/50 bg-gradient-to-br from-[#EFF8FA]/90 via-white to-[#F7FCFD] p-5"
          >
            <span
              aria-hidden
              className="absolute inset-y-0 left-0 w-1 bg-[#5DB6CE]"
              // className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-amber-400 via-[#5DB6CE] to-primary-200"
            />
            <div className="flex flex-wrap items-start gap-4 pl-3">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-[#C5E4ED]">
                <MaskedIcon
                  src="/images/memberships/document-add-outline.svg"
                  className="h-6 w-6 bg-primary-200"
                />
              </span>
              <div className="min-w-0 flex-1">
                <span className="inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-raleway font-semibold uppercase tracking-[0.14em] text-amber-800 ring-1 ring-amber-200">
                  Action needed
                </span>
                <h3 className="mt-2.5 font-raleway text-[20px] font-bold text-primary-200">
                  No agreement saved yet
                </h3>
                <p className="mt-1.5 max-w-3xl text-[14px] font-open-sans leading-relaxed text-[#4D4D4D]">
                  Create and save your membership agreement before members can review and sign. Start
                  from scratch or choose a template to get started.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={onStartFromScratch}
                    className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary-200 px-4 font-raleway text-[13px] font-semibold text-white transition-colors hover:bg-[#002a35]"
                  >
                    <EditIcon sx={{ fontSize: 17 }} />
                    Start from scratch
                  </button>
                  <button
                    type="button"
                    onClick={onUseTemplate}
                    className="inline-flex h-10 items-center gap-2 rounded-lg border border-primary-200 bg-white px-4 font-raleway text-[13px] font-semibold text-primary-200 transition-colors hover:bg-[#EFF8FA]"
                  >
                    <MaskedIcon
                      src="/images/memberships/library-outline.svg"
                      className="h-[17px] w-[17px] bg-primary-200"
                    />
                    Use template
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            role="status"
            aria-label="Community agreement saved"
            className="flex items-center gap-3 overflow-hidden rounded-xl border border-primary-300/35 bg-[#EFF8FA]/80 px-4 py-3.5 sm:gap-4"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white ring-1 ring-[#C5E4ED]">
              <CheckCircleIcon sx={{ fontSize: 20, color: "#5DB6CE" }} />
            </span>
            <span className="inline-flex shrink-0 rounded-full bg-primary-200/10 px-2.5 py-0.5 text-[11px] font-raleway font-semibold uppercase tracking-wide text-primary-200">
              Saved
            </span>
            <p className="min-w-0 truncate font-raleway text-[15px] font-bold text-[#333333] sm:text-[16px]">
              {agreementTitle}
            </p>
            {lastUpdatedLabel ? (
              <>
                {/* <span aria-hidden className="shrink-0 text-[#C5C5C5]">
                  ·
                </span> */}
                <p className="shrink-0 whitespace-nowrap text-[13px] font-open-sans text-[#666666]">
                  Last saved {lastUpdatedLabel}
                </p>
              </>
            ) : null}
            <button
              type="button"
              onClick={onViewAgreement}
              disabled={viewLoading}
              className="ml-auto inline-flex h-9 shrink-0 items-center gap-2 rounded-lg border border-primary-200 bg-white px-3.5 font-raleway text-[13px] font-semibold text-primary-200 transition-colors hover:bg-primary-200 hover:text-white disabled:cursor-wait disabled:opacity-60 sm:px-4"
            >
              <MaskedIcon
                src="/images/memberships/solar_document-text-linear.svg"
                className="h-[16px] w-[16px] bg-primary-200"
              />
              {viewLoading ? "Loading…" : "View Existing Agreement"}
            </button>
          </div>
        )}
      </div>
    </article>
  );
};

const AgreementTrackingEmptyState = ({ onUpload }: { onUpload: () => void }) => {
  return (
    <div className="flex flex-col items-center px-6 py-16 text-center sm:py-20">
      <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EFF8FA]">
        <MaskedIcon
          src="/images/memberships/document-medicine-bold.svg"
          className="h-8 w-8 bg-[#5DB6CE]"
        />
      </span>
      <h4 className="mt-5 font-raleway text-[18px] font-semibold text-primary-200">
        No agreement activity yet
      </h4>
      <p className="mt-2 max-w-md text-[14px] font-open-sans text-[#4D4D4D]">
        Once you publish an agreement and members start signing, their status, tier, and signature
        progress will appear in this table.
      </p>
      <button
        type="button"
        onClick={onUpload}
        className="mt-6 inline-flex h-11 items-center gap-2 rounded-lg bg-primary-200 px-5 font-raleway text-[14px] font-semibold text-white transition-colors hover:bg-[#002a35]"
      >
        <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />
        Upload Agreement
      </button>
    </div>
  );
};

export default MemberAgreement;
